import React, { useRef, useEffect, useState, useCallback } from 'react';
import { Button, Center, Loader, Stack, Text } from '@mantine/core';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibregl from 'maplibre-gl';
import { bbox } from '@turf/turf';

import { point } from '@turf/helpers';
import circle from '@turf/circle';
import LoginPage from './components/LoginPage';
import WachtwoordResetPage from './components/WachtwoordResetPage';
import RegisterPage from './components/RegisterPage';
import './App.css';
import {
  STATION_CONFIGURATIONS, getStationConfig, KLEUR_NVT,
  isOmbouwExpr, isVervangendExpr, geenOmbouwExpr,
} from './utils/stationConfig';
import { rotateOffset, offsetLatLng, createEnergyHouseGeoJSON } from './utils/energiehuisje';
import { toggleBasemap, gemeenteSlug, generateRegioColors } from './utils/mapHelpers';
import LegendaPaneel from './components/LegendaPaneel';
import MapToolbar from './components/MapToolbar';
import SearchBar from './components/SearchBar';
import AppContext from './contexts/AppContext';
import useAuth from './hooks/useAuth';
import useSessie from './hooks/useSessie';
import Sidebar from './components/Sidebar';
import SessieBalk from './components/SessieBalk';
import UserMenu from './components/UserMenu';
import NavigatiePad from './components/NavigatiePad';
import notify from './utils/notify';

// Bij ombouw wordt niets nieuws gebouwd, dus een footprint uit een kastmodel
// klopt daar per definitie niet. Die krijgt een icoon in dezelfde grijstint als
// de rand van de stip op buurtniveau. Een vervangend station is wél nieuwbouw en
// houdt gewoon zijn huisje.
const SYMBOOL_ICONEN = {
  'icoon-ombouw': `<svg xmlns="http://www.w3.org/2000/svg" width="64" height="64" viewBox="0 0 64 64">
    <circle cx="32" cy="32" r="29" fill="#868e96" stroke="#fff" stroke-width="4"/>
    <path d="M32 17 A 15 15 0 0 1 45 39" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round"/>
    <path d="M32 47 A 15 15 0 0 1 19 25" fill="none" stroke="#fff" stroke-width="6" stroke-linecap="round"/>
    <polygon points="33,10 33,24 23,17" fill="#fff"/>
    <polygon points="31,54 31,40 41,47" fill="#fff"/>
  </svg>`,
};

// Projecten -> stippen + omcirkeling voor de kaart op buurtniveau. Eén plek,
// zodat een verversing (bv. na het zetten van een akkoordstatus) exact dezelfde
// features oplevert als de eerste keer laden.
function bouwProjectStippen(features) {
  const stippen = { type: 'FeatureCollection', features: [] };
  (features || []).forEach((proj, idx) => {
    // Alleen stationsopgaven krijgen een stip.
    if (
      proj.properties.soort_project !== 'UITVOERING' || (
        proj.properties.type !== null
        && !proj.properties.type.toLowerCase().includes('station'))
    ) return;
    if (!proj.geometry || !proj.geometry.coordinates) return;
    const coord = proj.geometry.coordinates;
    const type = proj.properties.type || '';
    const p = point(coord, {
      ...proj.properties,
      featuretype: 'stip',
      id: proj.properties.projectnummer,
      locatie: proj.properties.locatie || idx,
      transparant: !type || !type.toLowerCase().includes('station'),
      rotatie1: typeof proj.properties.rotatie1 === 'number' ? proj.properties.rotatie1 : 0,
    });
    const c = circle(coord, 0.1, { steps: 64, units: 'kilometers' });
    c.properties = { featuretype: 'circle', id: proj.properties.projectnummer };
    stippen.features.push(p, c);
  });
  return stippen;
}

function App() {
  const mapContainer = useRef(null);
  const [editModus, setEditModus] = useState(false);
  const [level, setLevel] = useState('nederland');
  const dropzoneRef = useRef(null);
  const mapRef = useRef(null); // store map instance
  const statusRef = useRef(null);
  const gemeenteBoundsRef = useRef(null);
  const regioFeatureRef = useRef(null);
  const gemeenteFeatureRef = useRef(null);
  const buurtFeatureRef = useRef(null);
  const levelRef = useRef('nederland');
  const [jaren, setJaren] = useState([]);
  const [minJaar, setMinJaar] = useState(null);
  const [maxJaar, setMaxJaar] = useState(null);
  const [selectedYear, setSelectedYear] = useState(null);
  // Regio: toont de kaart de buurten gekleurd op uitvoeringsjaar? (toggle in de
  // RegioSidebar) Standaard uit -> gewoon de gemeenten van de regio.
  const [regioJaarActief, setRegioJaarActief] = useState(false);
  const [rectDragActive, setRectDragActive] = useState(false);
  const [rectCenter, setRectCenter] = useState(null); // [lng, lat]
  const [rectAngle, setRectAngle] = useState(0); // graden
  const rectSize = { width: 3, height: 2 }; // meters
  const [selectedProject, setSelectedProject] = useState(null); // State for sidebar project info
  // Versiebeheer: welke plaatsingsversie is uitgelicht (kaart ↔ tijdlijn).
  // null = standaard de nieuwste. versieRefresh dwingt een herlaad af na een
  // versiewijziging (slepen, kiezen).
  const [actieveVersieId, setActieveVersieId] = useState(null);
  const actieveVersieRef = useRef(null);
  const [versieRefresh, setVersieRefresh] = useState(0);
  // Na een sleep (nieuwe/gewijzigde plaatsing) willen we die nieuwe locatie
  // meteen uitgelicht hebben i.p.v. de oude selectie te houden.
  const selecteerNieuwsteRef = useRef(false);
  const bumpVersieRefresh = useCallback((selecteerNieuwste = false) => {
    if (selecteerNieuwste) selecteerNieuwsteRef.current = true;
    setVersieRefresh(n => n + 1);
  }, []);
  // Plaatsingsversies van het geselecteerde station (bron voor de kaarttekening)
  const projectVersiesRef = useRef([]);
  const projectPropsRef = useRef(null);
  // Marker voor een gezocht adres (PDOK); verdwijnt bij navigeren.
  const adresMarkerRef = useRef(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [stationstypeOptions, setStationstypeOptions] = useState([]);
  const [kleurstellingOptions, setKleurstellingOptions] = useState([]);
  const [gemeenteOptions, setGemeenteOptions] = useState([]);
  // Type werk (nieuwbouw/ombouw/vervangen station); stuurt ook de soort-kleuren.
  const [typeOptions, setTypeOptions] = useState([]);
  // KLIC-thema's die in de geladen buurt-datalagen voorkomen; stuurt de
  // ingekorte KLIC-legenda op projectniveau.
  const [klicThemas, setKlicThemas] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const {
    user, setUser, isLoading, showLogin, showRegister,
    setShowLogin, setShowRegister, fetchWithAuth,
    handleLogin, handleShowRegister, handleRegisterSuccess, handleLogout
  } = useAuth();
  // Meldingen via Mantine-notificaties (centraal, consistente stijl)
  const addToast = useCallback((message, type = 'info') => {
    notify(message, type);
  }, []);
  const userRef = useRef(user);
  useEffect(() => { userRef.current = user; }, [user]);
  const [buurtOptions, setBuurtOptions] = useState([]);
  const [selectedBuurt, setSelectedBuurt] = useState(null);
  // Belangentafel-sessie: state, start/stop en event-logging
  const sessie = useSessie({
    fetchWithAuth,
    buurtcode: selectedBuurt?.buurtcode,
    addToast,
  });
  const sessieLogRef = sessie.logEventRef;
  // Selectie/deselectie van een station automatisch als sessie-event loggen
  const vorigeSelectieRef = useRef(null);
  useEffect(() => {
    const vorige = vorigeSelectieRef.current;
    const huidige = selectedProject?.projectnummer || null;
    if (vorige === huidige) return;
    vorigeSelectieRef.current = huidige;
    if (huidige) {
      sessieLogRef.current('selectie', huidige, null);
    } else if (vorige) {
      sessieLogRef.current('deselectie', vorige, null);
    }
  }, [selectedProject, sessieLogRef]);
  // Niveauwissel tijdens een actieve sessie: loggen wanneer de gebruiker de
  // sessie-buurt verlaat (transcript hoort dan bij de buurt, niet een project)
  // en wanneer hij terugkeert. Opname loopt gewoon door.
  const vorigeNiveauRef = useRef(null);
  useEffect(() => {
    const vorige = vorigeNiveauRef.current;
    vorigeNiveauRef.current = level;
    if (!sessie.actieveSessie || vorige === level) return;
    const inBuurt = (niv) => niv === 'buurt' || niv === 'project';
    if (inBuurt(vorige) && !inBuurt(level)) {
      sessieLogRef.current('buurt-verlaten', null, null);
      notify('Let op: de sessie loopt nog door', 'warning');
    } else if (!inBuurt(vorige) && inBuurt(level)) {
      sessieLogRef.current('buurt-terug', null, null);
    }
  }, [level, sessie.actieveSessie, sessieLogRef]);
  const [buurtSidebarOpen, setBuurtSidebarOpen] = useState(false);
  const buurtSidebarOpenRef = useRef(buurtSidebarOpen);
  useEffect(() => { buurtSidebarOpenRef.current = buurtSidebarOpen; }, [buurtSidebarOpen]);
  // Add search state variables
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const [newStation, setNewStation] = useState(null); // {lng, lat}
  const [baseLayer, setBaseLayer] = useState('osm'); // 'osm' or 'satellite'
  // Optionele WMS-overlay 'percelen met recht' (MOOR); staat standaard uit en
  // is alleen op projectniveau in te schakelen via de toolbarknop.
  const [percelenOverlay, setPercelenOverlay] = useState(false);
  const baseLayerRef = useRef('osm');
  const [addStationMode, setAddStationMode] = useState(false);
  const [deleteStationMode, setDeleteStationMode] = useState(false);
  const [moveStationMode, setMoveStationMode] = useState(false);
  // Koppelen vervangend station -> te vervangen station: eerst het vervangende
  // project aanklikken, daarna het bestaande station. null = geen keuze gemaakt.
  const [koppelMode, setKoppelMode] = useState(false);
  const [koppelBron, setKoppelBron] = useState(null); // projectnummer
  // Voeg bovenaan de App component toe:
  const [selectedRegio, setSelectedRegio] = useState(null);
  const selectedRegioRef = useRef(selectedRegio);
  const [selectedGemeente, setSelectedGemeente] = useState(null);
  const selectedGemeenteRef = useRef(selectedGemeente);
  const [regiosLoaded, setRegiosLoaded] = useState(false);
  const regiosLoadedRef = useRef(false);
  useEffect(() => { selectedRegioRef.current = selectedRegio; }, [selectedRegio]);
  useEffect(() => { selectedGemeenteRef.current = selectedGemeente; }, [selectedGemeente]);
  useEffect(() => { levelRef.current = level; }, [level]);
  useEffect(() => { regiosLoadedRef.current = regiosLoaded; }, [regiosLoaded]);


  const onYearClick = useCallback((jaar) => {
    const map = mapRef.current;
    if (!map) return;
    if (selectedYear === jaar) {
      setSelectedYear(null);
      if (map.getLayer('buurten-fill')) {
        map.setPaintProperty('buurten-fill', 'fill-opacity', 0.3);
      }
    } else {
      setSelectedYear(jaar);
      if (map.getLayer('buurten-fill')) {
        map.setPaintProperty('buurten-fill', 'fill-opacity', [
          'case',
          ['==', ['to-number', ['get', 'netstrategie_jaartal']], jaar], 0.3,
          0.05
        ]);
      }
    }
  }, [selectedYear]);

  // Kleur de buurten-laag op uitvoeringsjaar (gedeeld door gemeente- en
  // regioniveau). Zet ook de jaar-state voor legenda + regio-dropdown.
  const kleurBuurtenOpJaar = useCallback((map, features) => {
    const extractJaar = (val) => {
      if (!val) return null;
      if (typeof val === 'number') return val;
      if (typeof val === 'string' && val.length >= 4) return Number(val.slice(0, 4));
      return null;
    };
    const jaren = (features || [])
      .map(f => extractJaar(f.properties.plan_start_uitvoering) || extractJaar(f.properties.netstrategie_jaartal))
      .filter(j => j !== null && !isNaN(j));
    if (!jaren.length) { setJaren([]); return; }
    const minJaar = Math.min(...jaren);
    const maxJaar = Math.max(...jaren);
    setJaren(jaren); setMinJaar(minJaar); setMaxJaar(maxJaar);
    if (!map.getLayer('buurten-fill')) return;
    if (minJaar === maxJaar) {
      map.setPaintProperty('buurten-fill', 'fill-color', '#377eb8');
    } else {
      map.setPaintProperty('buurten-fill', 'fill-color', [
        'interpolate', ['linear'], ['to-number', ['get', 'netstrategie_jaartal']],
        minJaar, '#377eb8',
        minJaar + (maxJaar - minJaar) * 0.25, '#4daf4a',
        minJaar + (maxJaar - minJaar) * 0.5, '#ffff33',
        minJaar + (maxJaar - minJaar) * 0.75, '#ff7f00',
        maxJaar, '#e41a1c',
      ]);
    }
    map.setPaintProperty('buurten-fill', 'fill-opacity', 0.3);
  }, []);

  // Regio jaar-view aan/uit (toggle in de RegioSidebar): laad+kleur de buurten
  // van de hele regio en demp de gemeente-fill, of ga terug naar de gemeenten.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || level !== 'regio') return;
    const regioNaam = regioFeatureRef.current?.properties?.regio;
    if (!regioNaam) return;
    if (regioJaarActief) {
      setSelectedYear(null);
      map.setFilter('buurten-fill', null);
      map.setFilter('buurten-line', null);
      (async () => {
        try {
          const resp = await fetchWithAuth(`/api/buurten?regio=${encodeURIComponent(regioNaam)}`);
          const data = await resp.json();
          if (map.getSource('buurten')) map.getSource('buurten').setData(data);
          kleurBuurtenOpJaar(map, data.features || []);
        } catch (e) { setJaren([]); }
      })();
      if (map.getLayer('gemeenten-fill')) map.setPaintProperty('gemeenten-fill', 'fill-opacity', 0);
      if (map.getLayer('buurten-fill')) map.setLayoutProperty('buurten-fill', 'visibility', 'visible');
      if (map.getLayer('buurten-line')) map.setLayoutProperty('buurten-line', 'visibility', 'visible');
    } else {
      if (map.getLayer('gemeenten-fill')) map.setPaintProperty('gemeenten-fill', 'fill-opacity', 0.35);
      if (map.getLayer('buurten-fill')) map.setLayoutProperty('buurten-fill', 'visibility', 'none');
      if (map.getLayer('buurten-line')) map.setLayoutProperty('buurten-line', 'visibility', 'none');
      setJaren([]);
      setSelectedYear(null);
    }
  }, [regioJaarActief, level, fetchWithAuth, kleurBuurtenOpJaar]);

  // Toggle edit mode via Alt+1
  useEffect(() => {
    const toggle = e => {
      if (e.altKey && e.key === '1') setEditModus(prev => !prev);
    };
    window.addEventListener('keydown', toggle);
    return () => window.removeEventListener('keydown', toggle);
  }, []);

  // Excel dropzone drag & drop handlers
  useEffect(() => {
    const dz = dropzoneRef.current;
    const st = statusRef.current;
    if (!dz || !st) return;
    const onDrag = e => {
      e.preventDefault();
      dz.style.background = e.type === 'dragover' ? '#e0f7fa' : '#f8f8f8';
    };
    ['dragover', 'dragleave', 'drop'].forEach(type => dz.addEventListener(type, onDrag));
    const onDrop = e => {
      e.preventDefault();
      dz.style.background = '#f8f8f8';
      const files = e.dataTransfer.files;
      if (files.length > 0 && (files[0].name.endsWith('.xlsx') || files[0].name.endsWith('.xlsm'))) {
        const formData = new FormData();
        formData.append('file', files[0]);
        st.textContent = 'Uploaden...';
        fetch('/upload_excel', { method: 'POST', body: formData })
          .then(r => r.json())
          .then(data => {
            st.textContent = data.success ? 'Excel geïmporteerd!' : 'Fout: ' + (data.error || 'Onbekend');
            if (data.success) addToast('Excel succesvol geïmporteerd', 'success');
            else addToast('Fout bij importeren: ' + (data.error || 'Onbekend'), 'error');
          })
          .catch(() => { st.textContent = 'Fout bij uploaden.'; addToast('Fout bij uploaden van Excel', 'error'); });
      } else {
        st.textContent = 'Alleen .xlsx of .xlsm toegestaan.';
      }
    };
    dz.addEventListener('drop', onDrop);
    return () => {
      ['dragover', 'dragleave', 'drop'].forEach(type => dz.removeEventListener(type, onDrag));
      dz.removeEventListener('drop', onDrop);
    };
  }, []);

  useEffect(() => {
    const dz = dropzoneRef.current;
    const st = statusRef.current;
    if (!dz || !st) return;
    const handler = e => {
      e.preventDefault();
      const files = e.dataTransfer.files;
      if (files.length > 0 && (files[0].name.endsWith('.xlsx') || files[0].name.endsWith('.xlsm'))) {
        const formData = new FormData();
        formData.append('file', files[0]);
        st.textContent = 'Uploaden...';
        fetch('/upload_excel', { method: 'POST', body: formData })
          .then(r => r.json())
          .then(data => {
            st.textContent = data.success ? 'Excel geïmporteerd!' : 'Fout: ' + (data.error || 'Onbekend');
            if (data.success) addToast('Excel succesvol geïmporteerd', 'success');
            else addToast('Fout bij importeren: ' + (data.error || 'Onbekend'), 'error');
          })
          .catch(() => { st.textContent = 'Fout bij uploaden.'; addToast('Fout bij uploaden van Excel', 'error'); });
      } else {
        st.textContent = 'Alleen .xlsx of .xlsm bestanden toegestaan.';
      }
    };
    dz.addEventListener('drop', handler);
    return () => dz.removeEventListener('drop', handler);
  }, []);

  // Map alleen initialiseren als user is ingelogd en mapContainer bestaat
  useEffect(() => {
    if (!user || !mapContainer.current) return;
    const map = new maplibregl.Map({
      container: mapContainer.current,
      style: {
        version: 8,
        glyphs: 'https://fonts.undpgeohub.org/fonts/{fontstack}/{range}.pbf',
        sprite: `${window.location.origin}/sprites/sprite`,
        sources: {
          osm: { type: 'raster', tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'], tileSize: 256, attribution: '&copy; OpenStreetMap contributors' },
          satellite: { type: 'raster', tiles: ['https://service.pdok.nl/hwh/luchtfotorgb/wmts/v1_0/Actueel_orthoHR/EPSG:3857/{z}/{x}/{y}.jpeg'], tileSize: 256, attribution: '&copy; PDOK / Kadaster', maxzoom: 19 },
          kadastraal: { type: 'geojson', data: { type: 'FeatureCollection', features: [] }, attribution: '&copy; Kadaster/PDOK' },
          regios: { type: 'geojson', data: '/api/regios' },
          gemeenten: { type: 'geojson', data: '/api/gemeenten' },
          buurten: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
          laagspanningsstations: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
          vervangingen: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
          projecten: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
          energiehuisjes: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
          boomkronen: { type: 'geojson', data: { type: 'FeatureCollection', features: [] } },
          // MOOR-WMS 'percelen met recht' — optionele overlay op projectniveau.
          // WMS 1.3.0 met CRS EPSG:3857 zodat de bbox-asvolgorde x,y is en
          // MapLibre's {bbox-epsg-3857} er direct in past.
          'moor-percelen': {
            type: 'raster',
            tiles: ['https://geo.moorwerkt.nl/geoserver/Stedin/ows?SERVICE=WMS&REQUEST=GetMap'
              + '&VERSION=1.3.0&LAYERS=pr_percelen%20met%20recht_region'
              + '&STYLES=polygon_opacity_0.6&FORMAT=image/png&TRANSPARENT=true'
              + '&WIDTH=256&HEIGHT=256&CRS=EPSG:3857&BBOX={bbox-epsg-3857}'],
            tileSize: 256,
            attribution: '&copy; MOOR',
          }
        },
        layers: [
          { id: 'osm', type: 'raster', source: 'osm', metadata: {niveau: ['nederland', 'regio', 'gemeente', 'buurt']}, layout: { visibility: 'visible' } },
          { id: 'satellite', type: 'raster', source: 'satellite', metadata: {niveau: ['nederland', 'regio', 'gemeente', 'buurt', 'project']}, layout: { visibility: 'none' } },
          // Bewust ZONDER metadata.niveau: toggleBasemap laat 'm dan met rust,
          // zodat deze overlay uit blijft tot de gebruiker 'm zelf aanzet.
          { id: 'moor-percelen', type: 'raster', source: 'moor-percelen', layout: { visibility: 'none' } },
          { id: 'kadastraal-line', type: 'line', source: 'kadastraal', metadata: {niveau: ['project']}, paint: { 'line-color': '#000000', 'line-width': 1.5, 'line-opacity': 0.7 }, layout: { visibility: 'visible' } },
          { id: 'kadastraal-bebouwing', type: 'fill', source: 'kadastraal', metadata: {niveau: ['project']}, paint: { 'fill-color': '#ff0000', 'fill-opacity': 0.5 }, filter: ['==', ['get', '_layer'], 'kadastralekaart:Bebouwing'] },
          { id: 'regios-fill', type: 'fill', source: 'regios', metadata: {niveau: ['nederland']}, paint: { 
            'fill-color': '#e41a1c', // Default color, will be dynamically updated
            'fill-opacity': 0.6 
          }, layout: { visibility: 'none' } }, // Start invisible until colors are loaded
          { id: 'regios-line', type: 'line', source: 'regios', metadata: {niveau: ['nederland']}, paint: { 'line-color': '#000000', 'line-width': 2 }, layout: { visibility: 'none' } },
          { id: 'gemeenten-fill', type: 'fill', source: 'gemeenten', metadata: {niveau: ['regio', 'gemeente', 'buurt']}, paint: { 'fill-color': '#ffe600', 'fill-opacity': 0.35 }, layout: { visibility: 'none' } },
          { id: 'gemeenten-line', type: 'line', source: 'gemeenten', metadata: {niveau: ['regio', 'gemeente', 'buurt']}, paint: { 'line-color': '#000000', 'line-width': 2 }, layout: { visibility: 'none' } },
          { id: 'buurten-hatch', type: 'fill', source: 'buurten', metadata: {niveau: ['gemeente']}, paint: { 'fill-pattern': 'hatch-pattern', 'fill-opacity': 0.5 }, filter: ['!=', ['get', 'bar_gereed'], null] },
          { id: 'buurten-fill', type: 'fill', source: 'buurten', metadata: {niveau: ['gemeente', 'buurt']}, paint: { 'fill-color': '#f28cb1', 'fill-opacity': 0.0 } },
          { id: 'buurten-line', type: 'line', source: 'buurten', metadata: {niveau: ['gemeente', 'buurt']}, paint: { 'line-color': '#000000', 'line-width': 1 } },
          // Bestaand netstation. Is het gekoppeld aan een vervangend station, dan
          // verdwijnt het: rode rand en vol getekend zodat het opvalt.
          { id: 'laagspanningsstations-point', type: 'circle', source: 'laagspanningsstations', metadata: {niveau: ['buurt']}, paint: { 'circle-radius': 10, 'circle-color': '#888', 'circle-stroke-width': 2, 'circle-stroke-color': ['case', ['==', ['get', 'wordt_vervangen'], true], '#e03131', '#fff'], 'circle-opacity': ['case', ['==', ['get', 'wordt_vervangen'], true], 0.8, 0.3] }, filter: ['==', ['get', 'featuretype'], 'stip'] },
          { id: 'laagspanningsstations-circle', type: 'fill', source: 'laagspanningsstations', metadata: {niveau: ['buurt']}, paint: { 'fill-color': '#888', 'fill-opacity': 0.2 }, filter: ['==', ['get', 'featuretype'], 'circle'] },
          // Lijn van het vervangende station naar elk station dat het vervangt.
          // Onder de stippen zodat die leesbaar blijven.
          { id: 'vervangingen-line', type: 'line', source: 'vervangingen', metadata: {niveau: ['buurt']}, paint: { 'line-color': '#1971c2', 'line-width': 2, 'line-dasharray': [2, 2] } },
          { id: 'projecten-point', type: 'circle', source: 'projecten', metadata: {niveau: ['buurt']},
        paint: {
          'circle-radius': 10,
          // Kleur = akkoordstatus van de gekozen/nieuwste locatie (de "Markeer
          // voor akkoord"-cyclus). Zelfde palet als BUURT_STATUSSEN in LegendaPaneel.
          'circle-color': [
            'match', ['get', 'akkoord_status'],
              'akkoord', '#39e600', // gifgroen (duidelijk te scheiden van het blauw)
              'afgekeurd', '#e03131', // rood
              'vervallen', '#000000', // zwart
              '#5dade2' // nog te bepalen (blauw)
          ],
          'circle-stroke-width': [
            'case',
            ['==', ['get', 'id'], selectedProject ? selectedProject.id : ''],
            4, // dikkere rand voor geselecteerde
            2  // standaard
          ],
          // Rand = soort werk. Selectie (geel) gaat er tijdelijk overheen.
          'circle-stroke-color': [
            'case',
              ['==', ['get', 'id'], selectedProject ? selectedProject.id : ''], '#ffff00', // geel voor geselecteerde
              isVervangendExpr(), '#1971c2', // blauw: vervangend station (nieuwbouw)
              isOmbouwExpr(), '#868e96', // grijs: ombouw
              '#fff' // wit: regulier nieuw station
          ],
          'circle-opacity': [
            'case',
              ['in', ['get', 'akkoord_status'], ['literal', ['akkoord', 'afgekeurd', 'vervallen']]],
              1.0,
              0.7 // nog te bepalen: iets vager
          ]
        },
        filter: ['==', ['get', 'featuretype'], 'stip']
          },
          { id: 'projecten-point-label', type: 'symbol', source: 'projecten', metadata: {niveau: ['buurt']},
        layout: {
          'text-field': [
            'slice',
            ['to-string', ['get', 'projectnummer']],
            ['-', ['length', ['to-string', ['get', 'projectnummer']]], 2],
            ['length', ['to-string', ['get', 'projectnummer']]]
          ],
          'text-size': 12,
          'text-font': ['Open Sans Regular'],
          'text-offset': [0, 0],
          'text-anchor': 'center',
          'text-allow-overlap': true,
          'text-ignore-placement': true
        },
        paint: {
          'text-color': '#000000',
          'text-halo-color': '#fff',
          'text-halo-width': 1.5
        },
        filter: ['==', ['get', 'featuretype'], 'stip']
          },
          { id: 'projecten-circle', type: 'fill', source: 'projecten', metadata: {niveau: ['buurt']}, paint: { 'fill-color': '#5dade2', 'fill-opacity': 0.15 }, filter: ['==', ['get', 'featuretype'], 'circle'] },
          { id: 'boomkronen-fill', type: 'fill', source: 'boomkronen', metadata: {niveau: ['project']}, paint: { 'fill-color': '#22c55e', 'fill-opacity': 0.6, 'fill-outline-color': '#16a34a' }, layout: { visibility: 'none' } },
          { id: 'boomkronen-line', type: 'line', source: 'boomkronen', metadata: {niveau: ['project']}, paint: { 'line-color': '#16a34a', 'line-width': 1.5 }, layout: { visibility: 'none' } },
          { id: 'energiehuisjes-parcel', type: 'fill', source: 'energiehuisjes', metadata: {niveau: ['project']}, paint: { 'fill-color': '#ffffff', 'fill-opacity': ['case', ['==', ['get', 'actief'], true], 0.25, 0.1], 'fill-outline-color': '#ffffff' }, filter: ['all', ['==', ['get', 'featuretype'], 'parcel'], geenOmbouwExpr()] },
          { id: 'energiehuisjes-building', type: 'fill', source: 'energiehuisjes', metadata: {niveau: ['project']}, paint: { 'fill-color': ['case', ['==', ['get', 'kleurstelling'], 'Antracietgrijs'], '#3F4F4F', ['==', ['get', 'kleurstelling'], 'Ommetselbaar'], '#8B4513', '#007a3d'], 'fill-opacity': ['case', ['==', ['get', 'actief'], true], 0.7, 0.3], 'fill-outline-color': '#000000' }, filter: ['all', ['==', ['get', 'featuretype'], 'building'], geenOmbouwExpr()] },
          { id: 'energiehuisjes-doors', type: 'line', source: 'energiehuisjes', metadata: {niveau: ['project']}, paint: { 'line-color': '#1976d2', 'line-width': 4, 'line-opacity': ['case', ['==', ['get', 'actief'], true], 1, 0.35] }, filter: ['all', ['==', ['get', 'featuretype'], 'door'], geenOmbouwExpr()] },
          { id: 'energiehuisjes-roosters', type: 'line', source: 'energiehuisjes', metadata: {niveau: ['project']}, paint: { 'line-color': '#800080', 'line-width': 2, 'line-opacity': ['case', ['==', ['get', 'actief'], true], 1, 0.35] }, filter: ['all', ['==', ['get', 'featuretype'], 'rooster'], geenOmbouwExpr()] },
          { id: 'energiehuisjes-vluchtweg', type: 'fill', source: 'energiehuisjes', metadata: {niveau: ['project']}, paint: { 'fill-color': '#ff0000', 'fill-opacity': ['case', ['==', ['get', 'actief'], true], 0.5, 0.2], 'fill-outline-color': '#000000' }, filter: ['all', ['==', ['get', 'featuretype'], 'vluchtweg'], geenOmbouwExpr()] },
          { id: 'energiehuisjes-mskabelvak', type: 'line', source: 'energiehuisjes', metadata: {niveau: ['project']}, paint: { 'line-color': '#ff0000', 'line-width': 2, 'line-dasharray': [2, 2], 'line-opacity': ['case', ['==', ['get', 'actief'], true], 1, 0.35] }, filter: ['all', ['==', ['get', 'featuretype'], 'MSkabelvak'], geenOmbouwExpr()] },
          { id: 'energiehuisjes-lskabelvak', type: 'line', source: 'energiehuisjes', metadata: {niveau: ['project']}, paint: { 'line-color': '#ff0000', 'line-width': 2, 'line-dasharray': [2, 2], 'line-opacity': ['case', ['==', ['get', 'actief'], true], 1, 0.35] }, filter: ['all', ['==', ['get', 'featuretype'], 'LSkabelvak'], geenOmbouwExpr()] },
          { id: 'energiehuisjes-label', type: 'symbol', source: 'energiehuisjes', metadata: {niveau: ['project']}, layout: { 'text-field': ['concat', ['case', ['==', ['get', 'gekozen'], true], '★ ', ''], 'v', ['to-string', ['get', 'versie_nr']]], 'text-size': ['case', ['==', ['get', 'actief'], true], 13, 11], 'text-font': ['Open Sans Regular'], 'text-anchor': 'center', 'text-allow-overlap': true, 'text-ignore-placement': true }, paint: { 'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 1, 'text-opacity': ['case', ['==', ['get', 'actief'], true], 1, 0.6] }, filter: ['all', ['==', ['get', 'featuretype'], 'building'], geenOmbouwExpr()] },
          // Ombouw/te slopen: icoon i.p.v. een footprint (die uit een kastmodel
          // wordt gegenereerd en nooit klopt met het bestaande station), met het
          // locatienummer eronder — het gewone label staat voor deze soorten uit.
          { id: 'energiehuisjes-symbool', type: 'symbol', source: 'energiehuisjes', metadata: {niveau: ['project']},
            layout: {
              'icon-image': 'icoon-ombouw',
              'icon-size': ['case', ['==', ['get', 'actief'], true], 0.5, 0.4],
              'icon-allow-overlap': true, 'icon-ignore-placement': true,
              'text-field': ['concat', ['case', ['==', ['get', 'gekozen'], true], '★ ', ''], 'v', ['to-string', ['get', 'versie_nr']]],
              'text-size': ['case', ['==', ['get', 'actief'], true], 13, 11],
              'text-font': ['Open Sans Regular'], 'text-anchor': 'top', 'text-offset': [0, 1.1],
              'text-allow-overlap': true, 'text-ignore-placement': true,
            },
            paint: {
              'icon-opacity': ['case', ['==', ['get', 'actief'], true], 1, 0.5],
              'text-color': '#ffffff', 'text-halo-color': '#000000', 'text-halo-width': 1,
              'text-opacity': ['case', ['==', ['get', 'actief'], true], 1, 0.6],
            },
            filter: ['all', ['==', ['get', 'featuretype'], 'building'], isOmbouwExpr()] },
        ]
      },
      center: [4.6913, 51.8416],
      zoom: 8,
      pitch: 0,
      dragRotate: false,
      pitchWithRotate: false,
      touchZoomRotate: true
    });
    // Schakel rotation via touch uit maar behoud zoom
    map.touchZoomRotate.disableRotation();
    mapRef.current = map;

    map.on('load', () => {
      // Voeg hatch-pattern toe vóór de laag die hem gebruikt
      const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 16 16'><rect width='16' height='16' fill='transparent'/><line x1='-8' y1='24' x2='24' y2='-8' stroke='#000' stroke-width='3' stroke-opacity='0.7'/></svg>`;
      const img = new window.Image(16, 16);
      img.onload = function() {
        if (!map.hasImage('hatch-pattern')) {
          map.addImage('hatch-pattern', img, { pixelRatio: 2 });
        }
      };
      img.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(svg);
      // Fallback: voeg image toe als deze ontbreekt
      map.on('styleimagemissing', function(e) {
        if (e.id === 'hatch-pattern') {
          map.addImage('hatch-pattern', img, { pixelRatio: 2 });
        }
      });

      // Iconen voor Ombouw/Te vervallen (energiehuisjes-symbool). Zelfde patroon:
      // laden als afbeelding en pas toevoegen als hij er nog niet is.
      const symboolAfbeeldingen = {};
      Object.entries(SYMBOOL_ICONEN).forEach(([naam, svgTekst]) => {
        const icoon = new window.Image(64, 64);
        symboolAfbeeldingen[naam] = icoon;
        icoon.onload = function() {
          if (!map.hasImage(naam)) map.addImage(naam, icoon, { pixelRatio: 2 });
        };
        icoon.src = 'data:image/svg+xml;utf8,' + encodeURIComponent(svgTekst);
      });
      map.on('styleimagemissing', function(e) {
        const icoon = symboolAfbeeldingen[e.id];
        if (icoon && icoon.complete && !map.hasImage(e.id)) {
          map.addImage(e.id, icoon, { pixelRatio: 2 });
        }
      });

      // Haal regios data op en stel dynamische kleuren in
      fetch('/api/regios')
        .then(r => r.json())
        .then(data => {
          if (data.features) {
            const regioNames = data.features.map(f => f.properties.regio);
            const dynamicColors = generateRegioColors(regioNames);
            
            // Update regio fill kleuren
            if (map.getLayer('regios-fill')) {
              map.setPaintProperty('regios-fill', 'fill-color', dynamicColors);
              // Maak regio layers nu zichtbaar met de juiste kleuren
              map.setLayoutProperty('regios-fill', 'visibility', 'visible');
              map.setLayoutProperty('regios-line', 'visibility', 'visible');
            }
            
            // Alleen voor Stedin gebruikers: stel nederland level in
            // Voor gemeente gebruikers: laat auto-drilldown het niveau bepalen
            if (userRef.current && userRef.current.gemeentenaam && userRef.current.gemeentenaam.toLowerCase() === 'stedin') {
              toggleBasemap('nederland', map, baseLayerRef.current);
            }
            
            // Mark regios as loaded
            setRegiosLoaded(true);
          }
        })
        .catch(err => {
          console.error('Error loading regios:', err);
          addToast('Fout bij laden van regio\'s', 'error');
          // Fallback: toch layers zichtbaar maken
          if (map.getLayer('regios-fill')) {
            map.setLayoutProperty('regios-fill', 'visibility', 'visible');
            map.setLayoutProperty('regios-line', 'visibility', 'visible');
          }
          toggleBasemap('nederland', map, baseLayerRef.current);
          setRegiosLoaded(true);
        });

      // Add hover tooltip for regios (instead of gemeenten)
      const popup = new maplibregl.Popup({ closeButton: false, closeOnClick: false });

      // Regio hover events (only show on Nederland level)
      map.on('mousemove', 'regios-fill', (e) => {
        if (levelRef.current === 'nederland') {
          map.getCanvas().style.cursor = 'pointer';
          const feature = e.features[0];
          const regioName = feature.properties.regio;
          const gemeentenCount = feature.properties.aantal_gemeenten;
          popup.setLngLat(e.lngLat)
            .setHTML(`<strong>Regio: ${regioName}</strong><br/>Gemeenten: ${gemeentenCount}`)
            .addTo(map);
        }
      });

      map.on('mouseleave', 'regios-fill', () => {
        if (levelRef.current === 'nederland') {
          map.getCanvas().style.cursor = '';
          popup.remove();
        }
      });

      // Gemeente hover events (show on regio, gemeente, buurt levels)
      map.on('mousemove', 'gemeenten-fill', (e) => {
        if (levelRef.current === 'regio' || levelRef.current === 'gemeente' || levelRef.current === 'buurt') {
          map.getCanvas().style.cursor = 'pointer';
          const feature = e.features[0];
          const name = feature.properties.gemeentenaam;
          popup.setLngLat(e.lngLat)
               .setHTML(`<strong>${name}</strong>`)
               .addTo(map);
        }
      });

      map.on('mouseleave', 'gemeenten-fill', () => {
        if (levelRef.current === 'regio' || levelRef.current === 'gemeente' || levelRef.current === 'buurt') {
          map.getCanvas().style.cursor = '';
          popup.remove();
        }
      });

      // Regio dblclick
      map.on('dblclick', 'regios-fill', (e) => {
        const feature = e.features[0];
        drillDown({ targetLevel: 'regio', feature });
        // Sluit alle bestaande zijpanelen
        setSelectedRegio(null);
        setSelectedGemeente(null);
        setSelectedBuurt(null);
        setSelectedProject(null);
        // Open zijpaneel met regio informatie
        setSelectedRegio(feature.properties);
        setSidebarOpen(true);
      });

      // Regio click for sidebar (with debounce for dblclick detection)
      let regioClickTimeout = null;
      map.on('click', 'regios-fill', (e) => {
        if (levelRef.current !== 'nederland') return;
        if (!e.features.length) return;
        
        // Store the feature data immediately
        const regio = e.features[0].properties;
        
        // Clear any existing timeout
        if (regioClickTimeout) {
          clearTimeout(regioClickTimeout);
          regioClickTimeout = null;
          return; // This was likely part of a double-click
        }
        
        // Set timeout to execute click after delay
        regioClickTimeout = setTimeout(() => {
          if (
            sidebarOpenRef.current &&
            selectedRegioRef.current &&
            selectedRegioRef.current.regio === regio.regio
          ) {
            setSidebarOpen(false);
            setSelectedRegio(null);
          } else {
            setSelectedRegio(regio);
            setSidebarOpen(true);
          }
          regioClickTimeout = null;
        }, 300); // 300ms delay to detect double-clicks
      });

      // Gemeente dblclick (work on regio level to drill down to gemeente level)
      map.on('dblclick', 'gemeenten-fill', (e) => {
        if (levelRef.current !== 'regio') return; // Only work on regio level

        // Only allow Stedin to drill down on gemeenten
        if (userRef.current && userRef.current.gemeentenaam && userRef.current.gemeentenaam.toLowerCase() !== 'stedin') {
          return; // Block drilldown for all non-Stedin users
        }
        const feature = e.features[0];
        drillDown({ targetLevel: 'gemeente', feature });
        // Sluit alle bestaande zijpanelen
        setSelectedRegio(null);
        setSelectedGemeente(null);
        setSelectedBuurt(null);
        setSelectedProject(null);
        // Open zijpaneel met gemeente informatie
        setSelectedGemeente(feature.properties);
        setSidebarOpen(true);
      });

      // Buurt dblclick
      map.on('dblclick', 'buurten-fill', (e) => {
        const feature = e.features[0];
        drillDown({ targetLevel: 'buurt', feature });
        // Sluit alle bestaande zijpanelen
        setSelectedRegio(null);
        setSelectedGemeente(null);
        setSelectedBuurt(null);
        setSelectedProject(null);
        // Open zijpaneel met buurt informatie
        setSelectedBuurt(feature.properties);
        setSidebarOpen(true);
      });

      // Project dblclick
      map.on('dblclick', 'projecten-point', (e) => {
        const feature = e.features[0];
        drillDown({ targetLevel: 'project', feature });
        // Sluit alle bestaande zijpanelen
        setSelectedRegio(null);
        setSelectedGemeente(null);
        setSelectedBuurt(null);
        setSelectedProject(null);
        // Open zijpaneel met project informatie
        setSelectedProject(feature.properties);
        setSidebarOpen(true);
      });

      // --- Mousemove/mouseleave voor buurten-fill (tooltip buurtnaam) ---
      let buurtPopup = null;
      map.on('mousemove', 'buurten-fill', e => {
        if (!e.features.length) return;
        map.getCanvas().style.cursor = 'pointer';
        const buurtnaam = e.features[0].properties.buurtnaam;
        if (buurtPopup) buurtPopup.remove();
        buurtPopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false })
          .setLngLat(e.lngLat)
          .setHTML(`<div class="buurt-label">${buurtnaam}</div>`)
          .addTo(map);
      });
      map.on('mouseleave', 'buurten-fill', () => {
        map.getCanvas().style.cursor = '';
        if (buurtPopup) { buurtPopup.remove(); buurtPopup = null; }
      });
      // --- einde mousemove/mouseleave buurten-fill ---

      // Mouseover voor projecten (stip en cirkel) - handlers zijn verplaatst naar apart useEffect

      // Disable default map dblclick zoom
      map.doubleClickZoom.disable();

      // Voeg direct daarna de click-handler toe (with debounce for dblclick detection):
      let projectClickTimeout = null;
      map.on('click', 'projecten-point', function handleProjectClick(e) {
        if (!e.features.length) return;
        
        // Store the feature data immediately
        const project = e.features[0].properties;
        
        // Clear any existing timeout
        if (projectClickTimeout) {
          clearTimeout(projectClickTimeout);
          projectClickTimeout = null;
          return; // This was likely part of a double-click
        }
        
        // Set timeout to execute click after delay
        projectClickTimeout = setTimeout(() => {
          if (
            sidebarOpenRef.current &&
            selectedProjectRef.current &&
            selectedProjectRef.current.projectnummer === project.projectnummer
          ) {
            setSidebarOpen(false);
            setSelectedProject(null);
          } else {
            setSelectedProject(project);
            setSidebarOpen(true);
          }
          projectClickTimeout = null;
        }, 300); // 300ms delay to detect double-clicks
      });
      // Voeg click-handler toe voor buurten-fill (with debounce for dblclick detection)
      let buurtClickTimeout = null;
      map.on('click', 'buurten-fill', function handleBuurtClick(e) {
        if (!e.features.length) return;
        
        // Store the feature data immediately
        const buurt = e.features[0].properties;
        
        // Clear any existing timeout
        if (buurtClickTimeout) {
          clearTimeout(buurtClickTimeout);
          buurtClickTimeout = null;
          return; // This was likely part of a double-click
        }
        
        // Set timeout to execute click after delay
        buurtClickTimeout = setTimeout(() => {
          if (
            sidebarOpenRef.current &&
            selectedBuurtRef.current &&
            selectedBuurtRef.current.buurtcode === buurt.buurtcode
          ) {
            setSidebarOpen(false);
            setSelectedBuurt(null);
          } else {
            setSelectedBuurt(buurt);
            setSidebarOpen(true);
          }
          buurtClickTimeout = null;
        }, 300); // 300ms delay to detect double-clicks
      });
      // Voeg click-handler toe voor gemeenten-fill (work on regio, gemeente, buurt levels, with debounce)
      let gemeenteClickTimeout = null;
      map.on('click', 'gemeenten-fill', function handleGemeenteClick(e) {
        if (levelRef.current === 'nederland' || !e.features.length) return; // Block on Nederland level
        if (levelRef.current !== 'regio' && levelRef.current !== 'gemeente' && levelRef.current !== 'buurt') return;
        
        // Store the feature data immediately
        const gemeente = e.features[0].properties;
        
        // Clear any existing timeout
        if (gemeenteClickTimeout) {
          clearTimeout(gemeenteClickTimeout);
          gemeenteClickTimeout = null;
          return; // This was likely part of a double-click
        }
        
        // Set timeout to execute click after delay
        gemeenteClickTimeout = setTimeout(() => {
          if (
            sidebarOpenRef.current &&
            selectedGemeenteRef.current &&
            selectedGemeenteRef.current.gemeentecode === gemeente.gemeentecode
          ) {
            setSidebarOpen(false);
            setSelectedGemeente(null);
          } else {
            // Regiovenster loslaten: dat heeft voorrang in het zijpaneel en
            // hield anders de gemeenteselectie onzichtbaar (klik leek dood)
            setSelectedRegio(null);
            setSelectedGemeente(gemeente);
            setSidebarOpen(true);
          }
          gemeenteClickTimeout = null;
        }, 300); // 300ms delay to detect double-clicks
      });
    });

    return () => map.remove();
  }, [user]);

  // Drag & drop projecten-point op buurt-niveau
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let dragProjectId = null;
    let isDragging = false;
    let dragProjectnummer = null;
    let dragProjectType = null;
    let lastProjectenData = null;

    function onMouseDown(e) {
      // Alleen slepen starten bij linkermuisknop én als moveStationMode actief is OF Shift ingedrukt
      if (level !== 'buurt') return;
      if (!e.features || !e.features.length) return;
      if (e.originalEvent && e.originalEvent.button !== 0) return; // Alleen links
      if (!e.originalEvent.shiftKey && !moveStationMode) return; // Slepen met Shift OF moveStationMode
      const props = e.features[0].properties;
      dragProjectId = props.id;
      dragProjectnummer = props.projectnummer;
      dragProjectType = props.featuretype;
      isDragging = true;
      map.getCanvas().style.cursor = 'grabbing';
      // Sla de huidige projecten-data op
      lastProjectenData = map.getSource('projecten')._data;
      e.preventDefault();
    }

    function onMouseMove(e) {
      if (!isDragging || !dragProjectId) return;
      // Clone de data zodat we kunnen muteren
      const projectenData = JSON.parse(JSON.stringify(lastProjectenData));
      // Zoek het punt en de cirkel
      const idxPoint = projectenData.features.findIndex(f => f.properties.id === dragProjectId && f.geometry.type === 'Point');
      const idxCircle = projectenData.features.findIndex(f => f.properties.id === dragProjectId && f.geometry.type === 'Polygon');
      if (idxPoint === -1 || idxCircle === -1) return;
      // Update punt-coordinaat
      projectenData.features[idxPoint].geometry.coordinates = [e.lngLat.lng, e.lngLat.lat];
      // Update cirkel-geometrie
      const circleFeature = circle([e.lngLat.lng, e.lngLat.lat], 0.1, { steps: 64, units: 'kilometers' });
      circleFeature.properties = projectenData.features[idxCircle].properties;
      projectenData.features[idxCircle] = circleFeature;
      map.getSource('projecten').setData(projectenData);
    }
    function onMouseUp() {
      if (isDragging && dragProjectnummer && dragProjectId) {
        // Haal de nieuwe coördinaat op
        const projectenData = map.getSource('projecten')._data;
        const feature = projectenData.features.find(f => f.properties.id === dragProjectId && f.geometry.type === 'Point');

        if (feature) {
          const newCoord = feature.geometry.coordinates;
          // Stuur naar backend via generiek endpoint
          fetchWithAuth('/api/projecten/adjust', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ projectnummer: dragProjectnummer, lokatie1: newCoord })
          });
          sessieLogRef.current('verschuiving', dragProjectnummer, 1);
        }
      }
      isDragging = false;
      dragProjectId = null;
      dragProjectnummer = null;
      dragProjectType = null;
      lastProjectenData = null;
      map.getCanvas().style.cursor = '';
    }
    // Alleen activeren op buurt-niveau
    if (level === 'buurt') {
      map.on('mousedown', 'projecten-point', onMouseDown);
      map.on('mousemove', onMouseMove);
      map.on('mouseup', onMouseUp);
    }
    return () => {
      map.off('mousedown', 'projecten-point', onMouseDown);
      map.off('mousemove', onMouseMove);
      map.off('mouseup', onMouseUp);
    };
  }, [level, moveStationMode]);

  // Cursor management voor project hover (apart useEffect voor moveStationMode dependency)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let projectPopup = null;

    function showProjectPopup(e) {
      if (!e.features.length) return;
      const props = e.features[0].properties;
      const html = `<b> ${props.projectnummer || ''}</b><br/>${props.type || ''}`;

      // Verwijder bestaande popup
      if (projectPopup) {
        projectPopup.remove();
        projectPopup = null;
      }

      projectPopup = new maplibregl.Popup({ closeButton: false, closeOnClick: false })
        .setLngLat(e.lngLat)
        .setHTML(html)
        .addTo(map);
    }

    function hideProjectPopup() {
      if (projectPopup) {
        projectPopup.remove();
        projectPopup = null;
      }
      map.getCanvas().style.cursor = '';
    }

    const onProjectMouseMove = (e) => {
      // Toon "grab" cursor als Shift ingedrukt OF moveStationMode actief is
      if ((e.originalEvent && e.originalEvent.shiftKey) || moveStationMode) {
        map.getCanvas().style.cursor = 'grab';
      } else {
        map.getCanvas().style.cursor = 'pointer';
      }
      showProjectPopup(e);
    };

    const onProjectMouseLeave = (e) => {
      map.getCanvas().style.cursor = '';
      hideProjectPopup();
    };

    map.on('mousemove', 'projecten-point', onProjectMouseMove);
    map.on('mouseleave', 'projecten-point', onProjectMouseLeave);

    return () => {
      map.off('mousemove', 'projecten-point', onProjectMouseMove);
      map.off('mouseleave', 'projecten-point', onProjectMouseLeave);
      // Cleanup popup bij unmount
      if (projectPopup) {
        projectPopup.remove();
        projectPopup = null;
      }
    };
  }, [moveStationMode, level]);

  // Drag & drop + rotatie energiehuisje op project-niveau
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let dragActive = false;
    let dragStartLngLat = null;
    let dragProjectnummer = null;
    let dragVersieId = null;   // welke plaatsingsversie wordt versleept
    let bewogen = false;       // klik (selecteren) vs sleep (nieuwe versie)
    let lastEnergiehuisjeData = null;
    let dragRotation = 0;
    let lastRotation = 0;
    let dragCenter = null;
    let scrollZoomEnabled = true;

    function getCurrentRotation(features) {
      const building = features.find(f => f.properties.featuretype === 'building' && f.properties.projectnummer === dragProjectnummer && f.properties.lokatie_versie_id === dragVersieId);
      return (building && typeof building.properties.rotation === 'number') ? building.properties.rotation : 0;
    }

    function getCurrentCenter(features) {
      const building = features.find(f => f.properties.featuretype === 'building' && f.properties.projectnummer === dragProjectnummer && f.properties.lokatie_versie_id === dragVersieId);
      if (building && building.geometry && building.geometry.coordinates && building.geometry.coordinates[0]) {
        const ring = building.geometry.coordinates[0];
        const uniqueRing = ring.slice(0, -1); // Verwijder laatste punt (duplicate van eerste)
        const lng = uniqueRing.reduce((sum, p) => sum + p[0], 0) / uniqueRing.length; // Delen door 4
        const lat = uniqueRing.reduce((sum, p) => sum + p[1], 0) / uniqueRing.length; // Delen door 4
        return [lng, lat];
      }
      return null;
    }

    function onMouseDown(e) {
      if (level !== 'project') return;
      if (!e.features || !e.features.length) return;
      const f = e.features[0];
      if (!['building', 'parcel'].includes(f.properties.featuretype)) return;
      dragActive = true;
      bewogen = false;
      dragStartLngLat = e.lngLat;
      dragProjectnummer = f.properties.projectnummer;
      // MapLibre laat null-properties vallen in event-features, dus een
      // placeholder-versie (nieuw station zonder echte versie-rij) komt hier als
      // undefined terug terwijl de brondata null bevat. Normaliseer naar null,
      // anders faalt de match (null !== undefined) en beweegt het station niet.
      dragVersieId = f.properties.lokatie_versie_id ?? null;
      lastEnergiehuisjeData = map.getSource('energiehuisjes')._data;
      dragRotation = getCurrentRotation(lastEnergiehuisjeData.features);
      lastRotation = dragRotation;
      dragCenter = getCurrentCenter(lastEnergiehuisjeData.features);
      map.getCanvas().style.cursor = 'grabbing';
      // Scroll zoom tijdelijk uitzetten tijdens draaien
      if (map.scrollZoom && map.scrollZoom.isEnabled()) {
        map.scrollZoom.disable();
        scrollZoomEnabled = true;
      } else {
        scrollZoomEnabled = false;
      }
      window.addEventListener('wheel', onWheel, { passive: false });
      e.preventDefault();
    }
    function onMouseMove(e) {
      if (!dragActive || !dragStartLngLat) return;
      // Nieuw center is waar de muis nu is t.o.v. het origineel
      const dx = e.lngLat.lng - dragStartLngLat.lng;
      const dy = e.lngLat.lat - dragStartLngLat.lat;
      if (Math.abs(dx) > 1e-6 || Math.abs(dy) > 1e-6) bewogen = true;
      // Bepaal het nieuwe center op basis van drag
      let newCenter = null;
      if (dragCenter) {
        newCenter = [dragCenter[0] + dx, dragCenter[1] + dy];
      } else {
        newCenter = [e.lngLat.lng, e.lngLat.lat];
      }
      const newData = JSON.parse(JSON.stringify(lastEnergiehuisjeData));
      newData.features.forEach(f => {
        if (f.properties.projectnummer !== dragProjectnummer) return;
        // Alleen de gesleepte versie beweegt mee; andere versies staan vast
        if (f.properties.lokatie_versie_id !== dragVersieId) return;
        // Gebruik altijd originele rechthoek-structuur: hergenereer met createEnergyHouseGeoJSON
        if (f.properties.featuretype === 'building' || f.properties.featuretype === 'parcel' || f.properties.featuretype === 'door' || f.properties.featuretype === 'vluchtweg' || f.properties.featuretype === 'rooster' || f.properties.featuretype === 'MSkabelvak' || f.properties.featuretype === 'LSkabelvak') {
          // Haal stationstype direct van de feature (nu opgeslagen tijdens initiële generatie)
          const stationstype = f.properties.stationstype;
          
          // Haal configuratie op basis van stationstype
          const config = getStationConfig(stationstype);
          
          // Let op: createEnergyHouseGeoJSON verwacht [lat, lng]
          const geo = createEnergyHouseGeoJSON([newCenter[1], newCenter[0]], config.width, config.length, config.buffer, config.doorSides, config.doorWidth, dragRotation, config.roosterSides, config.MSkabelvakSides, config.MSkabelvakLength, config.LSkabelvakSides, config.LSkabelvakLength);
          // Zoek juiste feature type met verbeterde deur matching
          let match = null;
          if (f.properties.featuretype === 'door' || f.properties.featuretype === 'vluchtweg' || f.properties.featuretype === 'rooster') {
            // Voor deuren, vluchtwegen en roosters: match op side en index, met fallback voor backwards compatibility
            const matches = geo.features.filter(gf => 
              gf.properties.featuretype === f.properties.featuretype && 
              gf.properties.side === f.properties.side
            );
            
            // Probeer exacte match op doorIndex of roosterIndex
            if (f.properties.featuretype === 'door' || f.properties.featuretype === 'vluchtweg') {
              match = matches.find(gf => gf.properties.doorIndex === f.properties.doorIndex);
              // Fallback: als geen exacte match en oude feature heeft geen doorIndex, neem eerste van deze side
              if (!match && f.properties.doorIndex === undefined && matches.length > 0) {
                match = matches[0];
              }
            } else if (f.properties.featuretype === 'rooster') {
              match = matches.find(gf => gf.properties.roosterIndex === f.properties.roosterIndex);
              // Fallback: als geen exacte match en oude feature heeft geen roosterIndex, neem eerste van deze side
              if (!match && f.properties.roosterIndex === undefined && matches.length > 0) {
                match = matches[0];
              }
            }
          } else if (f.properties.featuretype === 'MSkabelvak') {
            // Voor MSkabelvak: match op side, kabelvakIndex en lineIndex
            const matches = geo.features.filter(gf =>
              gf.properties.featuretype === 'MSkabelvak' &&
              gf.properties.side === f.properties.side &&
              gf.properties.kabelvakIndex === f.properties.kabelvakIndex
            );

            // Probeer exacte match op lineIndex
            match = matches.find(gf => gf.properties.lineIndex === f.properties.lineIndex);

            // Fallback: als geen exacte match op lineIndex, neem eerste van deze kabelvak
            if (!match && matches.length > 0) {
              match = matches[0];
            }
          } else if (f.properties.featuretype === 'LSkabelvak') {
            // Voor LSkabelvak: match op side, kabelvakIndex en lineIndex
            const matches = geo.features.filter(gf =>
              gf.properties.featuretype === 'LSkabelvak' &&
              gf.properties.side === f.properties.side &&
              gf.properties.kabelvakIndex === f.properties.kabelvakIndex
            );

            // Probeer exacte match op lineIndex
            match = matches.find(gf => gf.properties.lineIndex === f.properties.lineIndex);

            // Fallback: als geen exacte match op lineIndex, neem eerste van deze kabelvak
            if (!match && matches.length > 0) {
              match = matches[0];
            }
          } else {
            // Voor building/parcel: gewone match
            match = geo.features.find(gf => gf.properties.featuretype === f.properties.featuretype);
          }
          if (match) {
            f.geometry = match.geometry;
            f.properties.rotation = dragRotation;
          }
        }
      });
      map.getSource('energiehuisjes').setData(newData);
    }
    function onWheel(e) {
      if (!dragActive) return;
      e.preventDefault();
      dragRotation += (e.deltaY < 0 ? 2 : -2);
      if (dragRotation < 0) dragRotation += 360;
      if (dragRotation >= 360) dragRotation -= 360;
      // Forceer redraw met het actuele center (waar de muis nu is)
      if (lastEnergiehuisjeData && dragCenter && dragStartLngLat) {
        // Simuleer een move event op de laatst bekende muispositie
        const fakeMove = { lngLat: window._lastDragLngLat || dragStartLngLat };
        onMouseMove(fakeMove);
      }
    }
    function onMouseMoveWrapper(e) {
      // Sla de laatst bekende muispositie op voor wheel-rotatie
      window._lastDragLngLat = e.lngLat;
      onMouseMove(e);
    }
    function onMouseUp(e) {
      if (dragActive && dragProjectnummer && !bewogen) {
        // Klik zonder verslepen: deze versie uitlichten + de tijdlijn openklappen
        setActieveVersieId(dragVersieId);
        // Loopt er een belangentafel? Leg vast dat we vanaf nu déze locatie
        // bespreken (net als het aanklikken in de tijdlijn), zodat de
        // transcriptie per locatie gekoppeld wordt i.p.v. aan één locatie.
        if (dragVersieId != null) {
          sessieLogRef.current('versie-selectie', dragProjectnummer, null, dragVersieId);
        }
      } else if (dragActive && dragProjectnummer && bewogen) {
        const data = map.getSource('energiehuisjes')._data;
        const gebouw = data.features.find(f => f.properties.featuretype === 'building' && f.properties.projectnummer === dragProjectnummer && f.properties.lokatie_versie_id === dragVersieId);
        const rotation = dragRotation;
        if (gebouw && gebouw.geometry && gebouw.geometry.coordinates && gebouw.geometry.coordinates[0]) {
          const ring = gebouw.geometry.coordinates[0];
          const uniqueRing = ring.slice(0, -1); // Verwijder laatste punt (duplicate van eerste)
          const lng = uniqueRing.reduce((sum, p) => sum + p[0], 0) / uniqueRing.length; // Delen door 4
          const lat = uniqueRing.reduce((sum, p) => sum + p[1], 0) / uniqueRing.length; // Delen door 4
          // Verslepen = een nieuwe plaatsingsversie (append-only) op de nieuwe positie
          fetchWithAuth('/api/projecten/adjust', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              projectnummer: dragProjectnummer,
              lokatie1: [lng, lat],
              rotatie1: rotation,
            })
          }).then(() => bumpVersieRefresh(true));  // nieuwe locatie meteen selecteren
          sessieLogRef.current('verschuiving', dragProjectnummer, null);
        }
      }
      dragActive = false;
      dragStartLngLat = null;
      dragProjectnummer = null;
      lastEnergiehuisjeData = null;
      dragRotation = 0;
      lastRotation = 0;
      dragCenter = null;
      map.getCanvas().style.cursor = '';
      window.removeEventListener('wheel', onWheel);
      // Scroll zoom weer aanzetten indien nodig
      if (map.scrollZoom && !map.scrollZoom.isEnabled()) {
        map.scrollZoom.enable();
      }
    }
    if (level === 'project') {
      map.on('mousedown', 'energiehuisjes-building', onMouseDown);
      map.on('mousedown', 'energiehuisjes-parcel', onMouseDown);
      // Ombouw/te slopen tekenen geen building/parcel, dus daar is het icoon het
      // enige aangrijppunt. Die laag wordt door dezelfde building-feature gevoed,
      // dus onMouseDown krijgt featuretype 'building' en werkt ongewijzigd.
      map.on('mousedown', 'energiehuisjes-symbool', onMouseDown);
      map.on('mousemove', onMouseMoveWrapper);
      map.on('mouseup', onMouseUp);
    }
    return () => {
      map.off('mousedown', 'energiehuisjes-building', onMouseDown);
      map.off('mousedown', 'energiehuisjes-parcel', onMouseDown);
      map.off('mousedown', 'energiehuisjes-symbool', onMouseDown);
      map.off('mousemove', onMouseMoveWrapper);
      map.off('mouseup', onMouseUp);
      window.removeEventListener('wheel', onWheel);
      if (map.scrollZoom && !map.scrollZoom.isEnabled() && scrollZoomEnabled) {
        map.scrollZoom.enable();
      }
      delete window._lastDragLngLat;
    };
  }, [level]);

  // Alle plaatsingsversies van het station op de kaart tekenen. De uitgelichte
  // versie (actieveId, anders de nieuwste) is scherp via de 'actief'-property;
  // de overige versies zijn gedimd (paint-expressies gebruiken 'actief').
  const tekenVersies = useCallback((actieveId) => {
    const map = mapRef.current;
    if (!map || !map.getSource('energiehuisjes')) return;
    const versies = (projectVersiesRef.current || []).filter(v => !v.vluchtig);
    const props = projectPropsRef.current || {};
    if (!versies.length) {
      map.getSource('energiehuisjes').setData({ type: 'FeatureCollection', features: [] });
      return;
    }
    const nieuwste = versies.reduce((a, b) => (new Date(a.aangemaakt_op) >= new Date(b.aangemaakt_op) ? a : b));
    const prominentId = actieveId != null ? actieveId : nieuwste.id;
    const feats = [];
    versies.forEach(v => {
      // Type/kleur zijn per versie; val terug op het project als een versie ze
      // (nog) niet heeft.
      const vType = v.stationstype || props.stationstype;
      const vKleur = v.kleurstelling || props.kleurstelling;
      const config = getStationConfig(vType);
      const geo = createEnergyHouseGeoJSON(
        [v.lat, v.lng], config.width, config.length, config.buffer, config.doorSides,
        config.doorWidth, v.rotatie || 0, config.roosterSides, config.MSkabelvakSides,
        config.MSkabelvakLength, config.LSkabelvakSides, config.LSkabelvakLength,
      );
      geo.features.forEach(f => {
        f.properties.projectnummer = props.projectnummer;
        f.properties.lokatie_versie_id = v.id;
        f.properties.versie_nr = v.versie_nr;
        f.properties.gekozen = !!v.gekozen;
        f.properties.actief = v.id === prominentId;
        f.properties.rotation = v.rotatie || 0;
        f.properties.stationstype = vType;
        f.properties.kleurstelling = vKleur;
        // Soort werk (ombouw/te slopen) komt uit het projectveld `type`; nodig
        // om hier het icoon i.p.v. de footprint te tekenen.
        f.properties.type = props.type;
        feats.push(f);
      });
    });
    map.getSource('energiehuisjes').setData({ type: 'FeatureCollection', features: feats });
  }, []);

  // Laadt de plaatsingsversies van een project en tekent de energiehuisjes op de
  // kaart (projectniveau). Zonder versies wordt een verplaatsbaar placeholder-
  // station op het projectpunt getekend, zodat verslepen een eerste locatie maakt.
  // Wordt gebruikt door zowel de drilldown als het zoeken naar een station.
  const laadProjectVersies = useCallback(async (feature) => {
    const map = mapRef.current;
    if (!map) return;
    let versies = [];
    try {
      const vres = await fetchWithAuth(`/api/projecten/${encodeURIComponent(feature.properties.projectnummer)}/versies`);
      const vdata = await vres.json();
      if (vdata.success) versies = vdata.versies;
    } catch (e) { /* val terug op de puntgeometrie */ }
    if (!versies.length && feature.geometry && feature.geometry.coordinates) {
      versies = [{
        id: null, versie_nr: 1, lng: feature.geometry.coordinates[0],
        lat: feature.geometry.coordinates[1],
        rotatie: (feature.properties && Number(feature.properties.rotatie1)) || 0,
        gekozen: false, vluchtig: false, aangemaakt_op: new Date(0).toISOString(),
      }];
    }
    projectVersiesRef.current = versies;
    projectPropsRef.current = feature.properties;
    // Selecteer standaard de gekozen locatie, anders de nieuwste (zelfde regel
    // als de stip), zodat klikken op de stip meteen die locatie uitlicht.
    const zichtbaar = versies.filter(v => !v.vluchtig);
    const gekozen = zichtbaar.find(v => v.gekozen);
    const nieuwste = zichtbaar.length
      ? zichtbaar.reduce((a, b) => (new Date(a.aangemaakt_op) >= new Date(b.aangemaakt_op) ? a : b))
      : null;
    const doelId = gekozen ? gekozen.id : (nieuwste ? nieuwste.id : null);
    setActieveVersieId(doelId);
    tekenVersies(doelId);
    ['parcel', 'building', 'doors', 'roosters', 'vluchtweg', 'mskabelvak', 'lskabelvak', 'label', 'symbool'].forEach(l => {
      if (map.getLayer(`energiehuisjes-${l}`)) map.setLayoutProperty(`energiehuisjes-${l}`, 'visibility', 'visible');
    });
  }, [fetchWithAuth, tekenVersies]);

  // Verbindingslijnen vervangend station -> te vervangen station. Apart zodat de
  // kaart ook na koppelen/ontkoppelen ververst kan worden zonder hele drilldown.
  const laadVervangingen = useCallback(async (buurtCode) => {
    const map = mapRef.current;
    const bron = map && map.getSource('vervangingen');
    if (!bron || !buurtCode) return;
    try {
      const res = await fetchWithAuth(`/api/buurten/${encodeURIComponent(buurtCode)}/vervangingen`);
      const data = await res.json();
      bron.setData(data && data.features ? data : { type: 'FeatureCollection', features: [] });
    } catch (e) {
      bron.setData({ type: 'FeatureCollection', features: [] });
    }
  }, [fetchWithAuth]);

  // Stippen opnieuw ophalen zonder drilldown. Nodig zodra iets verandert wat de
  // stip kleurt of positioneert (akkoordstatus, type werk): anders zag je de
  // wijziging pas na een rondje naar een ander niveau en terug.
  const ververBuurtProjecten = useCallback(async () => {
    const map = mapRef.current;
    const bron = map && map.getSource('projecten');
    const buurtCode = buurtFeatureRef.current?.properties?.buurtcode;
    if (!bron || !buurtCode) return;
    try {
      const res = await fetchWithAuth(`/api/projecten?buurtcode=${encodeURIComponent(buurtCode)}`);
      const data = await res.json();
      bron.setData(bouwProjectStippen(data && data.features));
    } catch (e) { /* stippen blijven staan zoals ze waren */ }
  }, [fetchWithAuth]);

  // Bestaande stations opnieuw ophalen (voor de rode rand van 'wordt vervangen')
  // en de verbindingslijnen erbij. Na koppelen/ontkoppelen, zonder drilldown.
  const ververBuurtVervangingen = useCallback(async () => {
    const map = mapRef.current;
    const buurtCode = buurtFeatureRef.current?.properties?.buurtcode;
    if (!map || !buurtCode) return;
    try {
      const res = await fetchWithAuth(`/api/laagspanningsstations?buurtcode=${encodeURIComponent(buurtCode)}`);
      const data = await res.json();
      const stippen = { type: 'FeatureCollection', features: [] };
      (data.features || []).forEach((station, idx) => {
        if (!station.geometry || !station.geometry.coordinates) return;
        const coord = station.geometry.coordinates;
        const pid = `lsstation_${station.properties.id || idx}`;
        const p = point(coord, { featuretype: 'stip', id: pid, ...station.properties });
        const c = circle(coord, 0.1, { steps: 64, units: 'kilometers' });
        c.properties = { featuretype: 'circle', id: pid };
        stippen.features.push(p, c);
      });
      const bron = map.getSource('laagspanningsstations');
      if (bron) bron.setData(stippen);
    } catch (e) { /* stippen blijven staan zoals ze waren */ }
    await laadVervangingen(buurtCode);
  }, [fetchWithAuth, laadVervangingen]);

  // Laadt de buurt-specifieke GeoJSON-lagen (KLIC-kabels/leidingen e.d.) uit
  // /api/data/<buurt>/ en voegt ze als projectniveau-lagen toe. Idempotent: al
  // aanwezige lagen worden alleen ververst. Gebruikt door de drilldown én de
  // directe afdaling (buurt-lijst/zoeken).
  const laadBuurtDatalagen = useCallback(async (buurtCode) => {
    const map = mapRef.current;
    if (!map || !buurtCode) return;
    try {
      const sourcesResp = await fetch(`/api/data/${buurtCode}/sources.json`);
      if (!sourcesResp.ok) return;
      const sources = await sourcesResp.json();
      const styleResp = await fetch(`/api/data/${buurtCode}/style.json`);
      const styleConfig = styleResp.ok ? await styleResp.json() : [];
      // Data per bron ophalen én de aanwezige KLIC-thema's verzamelen (voor de
      // ingekorte legenda). We geven het geparste object aan MapLibre i.p.v. een
      // URL, dus geen extra fetch. Lukt de fetch niet, dan valt de bron terug op
      // de URL en tellen de thema's van die bron simpelweg niet mee.
      const themaSet = new Set();
      await Promise.all(Object.keys(sources).map(async name => {
        const localUrl = `/api/data/${buurtCode}/${name}.geojson`;
        let data = localUrl;
        try {
          const gj = await (await fetch(localUrl)).json();
          data = gj;
          (gj.features || []).forEach(ft => {
            const t = ft.properties && ft.properties.thema;
            if (t) themaSet.add(t);
          });
        } catch (e) { /* val terug op URL-bron */ }
        if (map.getSource(name)) map.getSource(name).setData(data);
        else map.addSource(name, { type: 'geojson', data });
      }));
      setKlicThemas(themaSet);
      if (!Array.isArray(styleConfig)) return;
      // Alle optionele KLIC-vlaggen aan (extra geometrie/detail/annotaties/etc.),
      // en het disciplinefilter uitschakelen zodat alle disciplines meekomen.
      const replacePlaceholders = (cond) => {
        if (['{{ShowExtraGeometries}}', '{{ShowExtraDetailInfo}}', '{{ShowAnnotations}}',
          '{{ShowMeasurements}}', '{{ShowSelectionLayer}}', '{{ShowPrecautionMeasures}}'].includes(cond)) {
          return true;
        }
        if (Array.isArray(cond) && cond[0] === 'in' && Array.isArray(cond[2])
            && cond[2][0] === 'literal' && cond[2][1] === '{{SelectedDisciplines}}') return true;
        if (Array.isArray(cond)) return cond.map(replacePlaceholders);
        return cond;
      };
      // Voeg de KLIC-lagen ONDER de energiehuisje-lagen in, zodat de
      // stationslocatie op projectniveau over de kabels/leidingen heen ligt.
      // beforeId = de eerste aanwezige energiehuisje-laag; is die er (nog) niet,
      // dan bovenop toevoegen (val terug op undefined).
      const ENERGIEHUISJE_LAGEN = ['energiehuisjes-parcel', 'energiehuisjes-building',
        'energiehuisjes-doors', 'energiehuisjes-roosters', 'energiehuisjes-vluchtweg',
        'energiehuisjes-mskabelvak', 'energiehuisjes-lskabelvak', 'energiehuisjes-label',
        'energiehuisjes-symbool'];
      const onderId = ENERGIEHUISJE_LAGEN.find(id => map.getLayer(id));
      styleConfig.sort((a, b) => a.order - b.order).forEach(group => {
        group.layers.forEach(layer => {
          if (map.getLayer(layer.id)) return;
          const layerDef = {
            id: layer.id, type: layer.type, source: layer.source,
            metadata: { niveau: ['project'] },
          };
          if (layer['source-layer']) layerDef['source-layer'] = layer['source-layer'];
          if (layer.minzoom !== undefined) layerDef.minzoom = layer.minzoom;
          if (layer.maxzoom !== undefined) layerDef.maxzoom = layer.maxzoom;
          if (layer.layout !== undefined) layerDef.layout = layer.layout;
          if (layer.paint !== undefined) layerDef.paint = layer.paint;
          if (layer.filter !== undefined) layerDef.filter = replacePlaceholders(layer.filter);
          if (map.getSource(layerDef.source)) map.addLayer(layerDef, onderId);
        });
      });
    } catch (e) {
      console.warn('Kon buurt-GeoJSONs niet laden:', e);
    }
  }, []);

  // Volledige afdaling naar projectniveau: kruimelpad-context afleiden, naar het
  // station vliegen, level = project, paneel tonen en het energiehuisje tekenen.
  // Gebruikt door het zoeken én het aanklikken van een station in de buurt-lijst.
  const daalAfNaarProject = useCallback(async (feature) => {
    const map = mapRef.current;
    if (!map || !feature.geometry?.coordinates) return;
    const [lng, lat] = feature.geometry.coordinates;
    const buurtCode = feature.properties.buurtcode;
    if (buurtCode && (!buurtFeatureRef.current
        || buurtFeatureRef.current.properties.buurtcode !== buurtCode)) {
      try {
        const bd = await (await fetchWithAuth(`/api/buurt-op-punt?lng=${lng}&lat=${lat}`)).json();
        if (bd.success && bd.feature) {
          buurtFeatureRef.current = bd.feature;
          const gmCode = bd.feature.properties.gemeentecode;
          const gs = await (await fetchWithAuth('/api/gemeenten')).json();
          const gemFeat = (gs.features || []).find(f => f.properties.gemeentecode === gmCode);
          if (gemFeat) {
            gemeenteFeatureRef.current = gemFeat;
            const rs = await (await fetchWithAuth('/api/regios')).json();
            const regioFeat = (rs.features || []).find(f => f.properties.regio === gemFeat.properties.regio);
            if (regioFeat) regioFeatureRef.current = regioFeat;
          }
        }
      } catch (e) { /* kruimelpad valt terug op bestaande context */ }
    }
    const flyToOptions = { center: [lng, lat], zoom: 18, pitch: 0, duration: 1500 };
    if (sidebarOpenRef.current) flyToOptions.offset = [175, 0];
    map.flyTo(flyToOptions);
    setLevel('project');
    // Geometry meegeven zodat de locatieviewer ook werkt als lokatie1 leeg is
    setSelectedProject({ ...feature.properties, geometry: feature.geometry });
    setSidebarOpen(true);
    await laadProjectVersies(feature);
    // Kaartlagen op projectniveau (satelliet + kadastraal/energiehuisje aan,
    // overige niveaus uit), dan de buurt-datalagen (KLIC-kabels/leidingen) laden
    // — zelfde volgorde als de drilldown, zodat KLIC-sublagen hun standaard-
    // zichtbaarheid houden.
    toggleBasemap('project', map, baseLayerRef.current);
    await laadBuurtDatalagen(feature.properties.buurtcode);
  }, [fetchWithAuth, setLevel, laadProjectVersies, laadBuurtDatalagen]);

  // Uitgelichte versie ↔ kaart: hertekent de scherpte en vliegt naar de versie.
  useEffect(() => {
    actieveVersieRef.current = actieveVersieId;
    if (levelRef.current !== 'project') return;
    tekenVersies(actieveVersieId);
    if (actieveVersieId != null) {
      const v = (projectVersiesRef.current || []).find(x => x.id === actieveVersieId);
      const map = mapRef.current;
      if (v && map) {
        const opts = { center: [v.lng, v.lat], zoom: 19, duration: 800 };
        if (sidebarOpenRef.current) opts.offset = [175, 0];
        map.flyTo(opts);
      }
    }
  }, [actieveVersieId, tekenVersies]);

  // WMS-overlay 'percelen met recht': alleen zichtbaar als de gebruiker 'm
  // aanzet én we op projectniveau zijn. Deze laag heeft bewust geen
  // metadata.niveau, dus toggleBasemap raakt 'm niet en sturen we 'm hier zelf.
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('moor-percelen')) return;
    const aan = percelenOverlay && level === 'project';
    map.setLayoutProperty('moor-percelen', 'visibility', aan ? 'visible' : 'none');
  }, [percelenOverlay, level]);

  // Na een versiewijziging (slepen/kiezen): versies herladen en opnieuw tekenen.
  useEffect(() => {
    if (versieRefresh === 0) return;
    if (levelRef.current !== 'project' || !projectPropsRef.current) return;
    (async () => {
      try {
        const res = await fetchWithAuth(`/api/projecten/${encodeURIComponent(projectPropsRef.current.projectnummer)}/versies`);
        const d = await res.json();
        if (d.success) {
          projectVersiesRef.current = d.versies;
          if (selecteerNieuwsteRef.current) {
            // Na een sleep: de zojuist geplaatste (nieuwste) locatie uitlichten
            selecteerNieuwsteRef.current = false;
            const zichtbaar = d.versies.filter(v => !v.vluchtig);
            const nieuwste = zichtbaar.length
              ? zichtbaar.reduce((a, b) => (new Date(a.aangemaakt_op) >= new Date(b.aangemaakt_op) ? a : b))
              : null;
            if (nieuwste) { setActieveVersieId(nieuwste.id); tekenVersies(nieuwste.id); }
            else tekenVersies(actieveVersieRef.current);
          } else {
            tekenVersies(actieveVersieRef.current);
          }
        }
      } catch (e) { /* huidige tekening blijft staan */ }
    })();
  }, [versieRefresh, fetchWithAuth, tekenVersies]);

  // Centrale drilldown functie
  const drillDown = useCallback(async ({ targetLevel, feature, bounds }) => {
    const map = mapRef.current;
    if (!map) return;
    // Adres-marker opruimen zodra we ergens heen navigeren
    if (adresMarkerRef.current) { adresMarkerRef.current.remove(); adresMarkerRef.current = null; }
    // Programmatische navigatie (zoeken, kruimelpad) kan binnenkomen terwijl de
    // stijl nog werk heeft; setFilter gooit dan "Style is not done loading".
    // Kort wachten op idle, maar nooit blijven hangen: na de timeout gaan we
    // door en zijn de kaartbewerkingen tolerant (zie try/catch hieronder).
    if (!map.isStyleLoaded()) {
      await new Promise(res => {
        const t = setTimeout(res, 1500);
        map.once('idle', () => { clearTimeout(t); res(); });
      });
    }

    // Alleen bij terug naar Nederland: buurtenlagen verbergen
    if (targetLevel === 'nederland') {
      // State en camera altijd bijwerken; kaartcosmetiek mag falen als de
      // stijl nog laadt (wordt bij de volgende interactie vanzelf hersteld).
      setLevel('nederland');
      map.flyTo({ center: [4.6913, 51.8416], zoom: 8, pitch: 0 });
      try {
        map.setFilter('gemeenten-fill', null);
        map.setFilter('gemeenten-line', null);
        map.setFilter('regios-fill', null);
        map.setFilter('regios-line', null);
        // Maak de source leeg
        if (map.getSource('laagspanningsstations')) {
          map.getSource('laagspanningsstations').setData({ type: 'FeatureCollection', features: [] });
        }
        toggleBasemap(targetLevel, map, baseLayerRef.current);
      } catch (e) {
        console.warn('Kaartlagen bijwerken uitgesteld (stijl laadt nog):', e.message);
      }
      return;
    }

    if (targetLevel === 'regio' && feature) {
      const regioNaam = feature.properties.regio;

      regioFeatureRef.current = feature;

      // Standaard: de gemeenten van de regio tonen (zoals voorheen). De buurten
      // per uitvoeringsjaar komen pas via de toggle in de RegioSidebar
      // (regioJaarActief); die jaar-view wordt hier gereset bij binnenkomst.
      map.setFilter('gemeenten-fill', ['==', ['get', 'regio'], regioNaam]);
      map.setFilter('gemeenten-line', ['==', ['get', 'regio'], regioNaam]);
      if (map.getLayer('gemeenten-fill')) map.setPaintProperty('gemeenten-fill', 'fill-opacity', 0.35);
      setRegioJaarActief(false);
      setJaren([]);
      setSelectedYear(null);

      // Zoom naar de regio
      const regioBbox = bbox(feature);
      // Recht van boven: alleen gemeenteniveau kantelt, regio niet
      const fitBoundsOptions = { padding: 50, pitch: 0, bearing: 0 };
      //if (sidebarOpen) {
        fitBoundsOptions.offset = [100, 0]; // Compenseer voor 350px breed zijpaneel
      //}
      map.fitBounds(regioBbox, fitBoundsOptions);

      setLevel('regio');
      toggleBasemap(targetLevel, map, baseLayerRef.current);
      return;
    }
    if (targetLevel === 'gemeente' && feature) {

      gemeenteFeatureRef.current = feature;
      // Filter gemeentenlaag zodat alleen de aangeklikte gemeente niet zichtbaar is
      const gmCode = feature.properties.gemeentecode;

      map.setFilter('buurten-fill', null);
      map.setFilter('buurten-line', null);
      map.setFilter('gemeenten-fill', ['!=', ['get', 'gemeentecode'], gmCode]);
      map.setFilter('gemeenten-line', ['!=', ['get', 'gemeentecode'], gmCode]);
      // Herstel de gemeente-fill (regioniveau dempt 'm naar 0)
      if (map.getLayer('gemeenten-fill')) map.setPaintProperty('gemeenten-fill', 'fill-opacity', 0.35);
      
      // Expliciet regio layers verbergen op gemeente niveau
      if (map.getLayer('regios-fill')) {
        map.setLayoutProperty('regios-fill', 'visibility', 'none');
      }
      if (map.getLayer('regios-line')) {
        map.setLayoutProperty('regios-line', 'visibility', 'none');
      }
      
      setSelectedProject(null); // Deselecteer project bij gemeente selectie
      setSelectedBuurt(null); // Deselecteer buurt bij gemeente selectie
      // Zoom op gemeente, recht van boven (geen tilt)
      const gemeenteBbox = bounds || bbox(feature);
      const fitBounds = [[gemeenteBbox[0], gemeenteBbox[1]], [gemeenteBbox[2], gemeenteBbox[3]]];
      const fitBoundsOptions = { padding: 20, pitch: 0 };
      //if (sidebarOpen) {
        fitBoundsOptions.offset = [100, 0]; // Compenseer voor 350px breed zijpaneel
      //}
      map.fitBounds(fitBounds, fitBoundsOptions);
      gemeenteBoundsRef.current = fitBounds;
      // Laad buurten van deze gemeente
      const response = await fetchWithAuth(`/api/buurten?gm_code=${gmCode}`);
      const data = await response.json();
      map.getSource('buurten').setData(data);
      setBuurtOptions(
        data.features
          .map(f => ({
            naam: f.properties.buurtnaam,
            code: f.properties.buurtcode
          }))
          .sort((a, b) => a.naam.localeCompare(b.naam))
      );
      // Kleur buurten op uitvoeringsjaar (gedeelde helper met regioniveau)
      kleurBuurtenOpJaar(map, data.features);

      // 3D-staafdiagrammen per buurt verwijderd (punt 1). Niet-Stedin gebruikers
      // zien geen jaar-kleuring maar een arcering.
      const isStedinGebruiker = userRef.current && userRef.current.gemeentenaam
        && userRef.current.gemeentenaam.toLowerCase() === 'stedin';
      if (!isStedinGebruiker) {
        if (map.getLayer('buurten-fill')) map.setPaintProperty('buurten-fill', 'fill-opacity', 0);
        if (map.getLayer('buurten-hatch')) map.setLayoutProperty('buurten-hatch', 'visibility', 'visible');
      }

      setLevel('gemeente');
      toggleBasemap(targetLevel, map, baseLayerRef.current);

      return;
    }

    if (targetLevel === 'buurt' && feature) {
      buurtFeatureRef.current = feature;

      // Bij een directe sprong (zoeken/adres) komt de buurt niet via het
      // gemeente- en regioniveau binnen, waardoor het kruimelpad de vorige
      // (verkeerde) regio/gemeente toont. Leid de juiste context af uit de
      // gemeenten/regios-bronnen (dezelfde als de kaart, browser-cached). Alleen
      // nodig als de huidige gemeente-context niet bij deze buurt hoort.
      const gmCode = feature.properties.gemeentecode;
      if (gmCode && (!gemeenteFeatureRef.current
          || gemeenteFeatureRef.current.properties.gemeentecode !== gmCode)) {
        try {
          const gs = await (await fetchWithAuth('/api/gemeenten')).json();
          const gemFeat = (gs.features || []).find(f => f.properties.gemeentecode === gmCode);
          if (gemFeat) {
            gemeenteFeatureRef.current = gemFeat;
            const rs = await (await fetchWithAuth('/api/regios')).json();
            const regioFeat = (rs.features || []).find(f => f.properties.regio === gemFeat.properties.regio);
            if (regioFeat) regioFeatureRef.current = regioFeat;
          }
        } catch (e) { /* kruimelpad valt terug op bestaande context */ }
      }

      // Eerst: zoom op buurt, filter pas daarna toepassen
      const buurtBbox = bounds || bbox(feature);
      const fitBoundsOptions = { padding: 20, pitch: 0, bearing: 0 };
      //if (sidebarOpen) {
        fitBoundsOptions.offset = [100, 0]; // Compenseer voor 350px breed zijpaneel
      //}
      map.fitBounds(
        [[buurtBbox[0], buurtBbox[1]], [buurtBbox[2], buurtBbox[3]]],
        fitBoundsOptions
      );

      // Niveau en selectie direct zetten: ook als het laden van lagen hieronder
      // hapert blijft de UI-state (sessiebalk, zijvenster) consistent. Nodig
      // voor de zoek-route, die niet via het gemeente-niveau binnenkomt.
      setLevel('buurt');
      // Project deselecteren: anders blijft het zijpaneel het vorige station
      // tonen (Sidebar kiest ProjectSidebar zolang selectedProject gezet is),
      // bv. bij rechtsklik terug van project- naar buurtniveau.
      setSelectedProject(null);
      setSelectedBuurt(feature.properties);

      setTimeout(async () => {
        // Directe sprong (bv. via zoeken) kan de kaartstijl nog ladend treffen
        if (!map.getSource('projecten')) {
          await new Promise(res => map.once('idle', res));
        }
        const buurtCode = feature.properties.buurtcode;
        const gmCode = feature.properties.gemeentecode;
        //Filter: haal alleen de huidige gemeente uit de gemeentenlaag
        if (map.getLayer('gemeenten-fill')) {
          map.setFilter('gemeenten-fill', ['!=', ['get', 'gemeentecode'], gmCode]);
        }
        if (map.getLayer('gemeenten-line')) {
          map.setFilter('gemeenten-line', ['!=', ['get', 'gemeentecode'], gmCode]);
        }

        // --- ALWAYS show buurten-fill and buurten-line except for selected buurt ---
        if (map.getLayer('buurten-fill')) {
          map.setLayoutProperty('buurten-fill', 'visibility', 'visible');
          map.setFilter('buurten-fill', ['!=', ['get', 'buurtcode'], buurtCode]);
          map.setPaintProperty('buurten-fill', 'fill-color', '#777777');
          map.setPaintProperty('buurten-fill', 'fill-opacity', 0.5);
        }
        if (map.getLayer('buurten-line')) {
          map.setLayoutProperty('buurten-line', 'visibility', 'visible');
          map.setFilter('buurten-line', ['!=', ['get', 'buurtcode'], buurtCode]);
        }
        // --- END always show buurten-fill/line except selected ---

        // --- Projecten ophalen en plotten ---
        const resp = await fetchWithAuth(`/api/projecten?buurtcode=${encodeURIComponent(buurtCode)}`);
        const data = await resp.json();
        // Vormgeving projecten als stip + circle
        const stippenData = bouwProjectStippen(data && data.features);

        const projectenBron = map.getSource('projecten');
        if (projectenBron) projectenBron.setData(stippenData);
        // Pas de opacity van de stippen aan op basis van transparant property

        const resp2 = await fetchWithAuth(`/api/laagspanningsstations?buurtcode=${encodeURIComponent(buurtCode)}`);
        const data2 = await resp2.json();
        // Vormgeving projecten als stip + circle
        const stippenData2 = { type: 'FeatureCollection', features: [] };
        if (data2 && data2.features && data2.features.length) {
          const turf = window.turf || require('@turf/turf');
          data2.features.forEach((station, idx) => {
            if (!station.geometry || !station.geometry.coordinates) return;
            const coord = station.geometry.coordinates;
            const key = station.properties.id || idx;
            const pid = `lsstation_${key}`;
            const p = point(coord, {
              featuretype: 'stip',
              id: pid,
              ...station.properties
            });
            const c = circle(coord, 0.1, { steps: 64, units: 'kilometers' });
            c.properties = { featuretype: 'circle', id: pid };
            stippenData2.features.push(p, c);
          });
        }
        const lssBron = map.getSource('laagspanningsstations');
        if (lssBron) lssBron.setData(stippenData2);
        await laadVervangingen(buurtCode);
        toggleBasemap(targetLevel, map, baseLayerRef.current);
        // Maak projecten-point en laagspanningsstations-point lagen zichtbaar in buurt-view
        if (map.getLayer('projecten-point')) {
          map.setLayoutProperty('projecten-point', 'visibility', 'visible');
        }
        if (map.getLayer('laagspanningsstations-point')) {
          map.setLayoutProperty('laagspanningsstations-point', 'visibility', 'visible');
        }
        // Verberg energiehuisjes-lagen op buurtniveau
        if (map.getLayer('energiehuisjes-parcel')) map.setLayoutProperty('energiehuisjes-parcel', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-building')) map.setLayoutProperty('energiehuisjes-building', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-doors')) map.setLayoutProperty('energiehuisjes-doors', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-roosters')) map.setLayoutProperty('energiehuisjes-roosters', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-vluchtweg')) map.setLayoutProperty('energiehuisjes-vluchtweg', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-mskabelvak')) map.setLayoutProperty('energiehuisjes-mskabelvak', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-lskabelvak')) map.setLayoutProperty('energiehuisjes-lskabelvak', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-label')) map.setLayoutProperty('energiehuisjes-label', 'visibility', 'none');
        if (map.getLayer('energiehuisjes-symbool')) map.setLayoutProperty('energiehuisjes-symbool', 'visibility', 'none');
      }, 400); // wacht tot na fitBounds animatie
      return;
    }
    if (targetLevel === 'project' && feature) {
      const buurtCode = feature.properties.buurtcode;

      // Zoom en centreer op het projectpunt
      if (feature.geometry && feature.geometry.type === 'Point') {
        const [lng, lat] = feature.geometry.coordinates;
        const flyToOptions = { center: [lng, lat], zoom: 19, pitch: 0 };
        if (sidebarOpen) {
          flyToOptions.offset = [175, 0]; // Compenseer voor 350px breed zijpaneel
        }
        map.flyTo(flyToOptions);
      }
      // Energiehuisjes uit de plaatsingsversies tekenen (of placeholder-station)
      await laadProjectVersies(feature);

      setLevel('project');
      toggleBasemap(targetLevel, map, baseLayerRef.current);
      await laadBuurtDatalagen(buurtCode);
    }
  }, [setLevel, setJaren, setMinJaar, setMaxJaar, laadProjectVersies, laadBuurtDatalagen, laadVervangingen]);

  // --- AUTO-DRILLDOWN OP BASIS VAN URL ---
  useEffect(() => {
    if (!user || !mapRef.current) return;
    // Alleen drilldown toestaan naar eigen gemeente (niet voor Stedin)
    // if (!user.gemeentenaam ) return; //|| user.gemeentenaam.toLowerCase() === 'stedin'
    const pathParts = window.location.pathname.split('/').filter(Boolean);
    if (pathParts.length > 0) {
      const [gemeenteSlug, buurtSlug] = pathParts;
      // Check of gemeente in URL overeenkomt met user.gemeentenaam
      const userSlug = user.gemeentenaam.replace(/ /g, '_').replace(/[^a-zA-Z0-9_]/g, '').toLowerCase();
      if (!user.gemeentenaam.toLowerCase() === 'stedin' && gemeenteSlug.toLowerCase() !== userSlug) return; // Blokkeer toegang tot andere gemeenten

      // Drilldown naar eigen gemeente en buurt
      fetch('/api/gemeenten')
        .then(r => r.json())
        .then(gemeentenData => {
          const gemeenteFeature = gemeentenData.features.find(
            f => f.properties.gemeentenaam &&
              f.properties.gemeentenaam.toLowerCase() === gemeenteSlug.toLowerCase()
          );
          if (!gemeenteFeature) return;
          drillDown({ targetLevel: 'gemeente', feature: gemeenteFeature });
          if (pathParts.length > 1) {
          fetchWithAuth(`/api/buurten?gm_code=${gemeenteFeature.properties.gemeentecode}`)
            .then(r => r.json())
            .then(buurtenData => {
              const buurtFeature = buurtenData.features.find(
                f => f.properties.buurtnaam &&
                  buurtSlug.replace(/_/g, ' ').toLowerCase() === f.properties.buurtnaam.toLowerCase()
              );
              if (buurtFeature) {
                drillDown({ targetLevel: 'buurt', feature: buurtFeature });
              }
            })
            .catch(err => {
              console.error('Error loading buurten:', err);
              addToast('Fout bij laden van buurten', 'error');
            });
          }
        })
        .catch(err => {
          console.error('Error loading gemeenten:', err);
          addToast('Fout bij laden van gemeenten', 'error');
        });
    } else {
      // Alleen drilldown als gemeentenaam NIET 'Stedin' is
      // Wacht tot regios geladen zijn
      const waitForRegiosAndDrilldown = () => {
        if (!regiosLoadedRef.current) {
          setTimeout(waitForRegiosAndDrilldown, 100);
          return;
        }
        
        const map = mapRef.current;
        if (!map || !map.getSource('gemeenten') || !map.getSource('regios')) return;
        
        (async () => {
          // Haal gemeenten-data op
          let gemeentenData = null;
          try {
            const resp = await fetch('/api/gemeenten');
            gemeentenData = await resp.json();
          } catch (e) {
            console.error('Error loading gemeenten for drilldown:', e);
            addToast('Fout bij laden van gemeenten', 'error');
            return;
          }
          
          // Zoek de feature met de juiste gemeentenaam
          const feature = gemeentenData.features.find(
            f => f.properties.gemeentenaam &&
                f.properties.gemeentenaam.toLowerCase() === user.gemeentenaam.toLowerCase()
          );
          
          if (feature) {
            // Voor gemeente gebruikers: direct naar gemeente niveau (sla regio over)
            drillDown({ targetLevel: 'gemeente', feature });
          }
        })();
      };
      
      // Start waiting for regios
      waitForRegiosAndDrilldown();
    }
  }, [user, drillDown]);

  // Navigate back up one level
  const goBack = useCallback(() => {
    if (level === 'project') {
      // Reset baseLayer naar osm bij terugkeren van project niveau
      setBaseLayer('osm');
      baseLayerRef.current = 'osm';
      drillDown({ targetLevel: 'buurt', feature: buurtFeatureRef.current });
    } else if (level === 'buurt') {
      drillDown({ targetLevel: 'gemeente', feature: gemeenteFeatureRef.current });
    } else if (level === 'gemeente') {
      drillDown({ targetLevel: 'regio', feature: regioFeatureRef.current });
    } else if (level === 'regio') {
      drillDown({ targetLevel: 'nederland' });
    }
  }, [level, drillDown]);

  // Right-click on map: korte klik = uitzoomen, lange klik = tilten
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    let rightClickStartTime = null;
    let isRightClicking = false;

    const onMouseDown = (e) => {
      if (e.button === 2) { // Rechtermuisknop
        rightClickStartTime = Date.now();
        isRightClicking = true;
      }
    };

    const onMouseUp = (e) => {
      if (e.button === 2 && isRightClicking) { // Rechtermuisknop
        const clickDuration = Date.now() - rightClickStartTime;
        isRightClicking = false;

        if (clickDuration < 500) { // Korte klik (< 500ms) = uitzoomen
          e.preventDefault();
          goBack();
        }
        // Lange klik (>= 500ms) = laat MapLibre's tilt functionaliteit toe
      }
    };

    const onContextMenu = (e) => {
      // Prevent default context menu, maar laat tilt functionaliteit toe
      e.preventDefault();
    };

    const canvas = map.getCanvas();
    canvas.addEventListener('mousedown', onMouseDown);
    canvas.addEventListener('mouseup', onMouseUp);
    canvas.addEventListener('contextmenu', onContextMenu);

    return () => {
      canvas.removeEventListener('mousedown', onMouseDown);
      canvas.removeEventListener('mouseup', onMouseUp);
      canvas.removeEventListener('contextmenu', onContextMenu);
    };
  }, [level, goBack]);

  // Ophalen unieke opties voor dropdowns ná login
  useEffect(() => {
    if (!user) return;
    fetchWithAuth('/api/projecten/options')
      .then(r => r.json())
      .then(data => {
        // Stationstype is puur het kastmodel; de soort werk (ombouw/te slopen)
        // zit in `type`. 'n.v.t.' blijft als kleurstelling beschikbaar voor
        // stations waar geen nieuw huisje wordt ingekleurd.
        setStationstypeOptions(data.stationstype || []);
        setKleurstellingOptions([...new Set([...(data.kleurstelling || []), KLEUR_NVT])]);
        setGemeenteOptions(data.gemeenteakkoord || []);
        setTypeOptions(data.type || []);
      })
      .catch(err => {
        console.error('Error loading options:', err);
        addToast('Fout bij laden van opties', 'error');
      });
  }, [user]);

  // Handler voor wijzigen van projectveld
  const handleProjectFieldChange = (field, value) => {
    if (!selectedProject) return;
    const nieuwProject = { ...selectedProject, [field]: value };
    setSelectedProject(nieuwProject);
    // Update backend direct
    fetchWithAuth('/api/projecten/adjust', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ projectnummer: selectedProject.projectnummer, [field]: value })
    })
      .then(r => r.json())
      .then(data => {
        if (data && data.success && data.properties) {
          setSelectedProject(prev => ({ ...prev, ...data.properties }));
          // Pas ook de feature properties aan in de projecten bron op de kaart
          const map = mapRef.current;
          if (map && map.getSource('projecten')) {
            const projectenData = map.getSource('projecten')._data;
            if (projectenData && projectenData.features) {
              const idx = projectenData.features.findIndex(f => f.properties.projectnummer === data.properties.projectnummer);
              if (idx !== -1) {
                projectenData.features[idx].properties[field] = data.properties[field];
                map.getSource('projecten').setData(projectenData);
              }
            }
          }
        }
      })
      .catch(err => {
        console.error('Error saving project field:', err);
        addToast('Fout bij opslaan van projectwijziging', 'error');
      });
  };

  useEffect(() => {
    const map = mapRef.current;
    if (!map || !map.getLayer('projecten-point')) return;
    map.setPaintProperty('projecten-point', 'circle-stroke-width', [
      'case',
      ['==', ['get', 'id'], selectedProject ? selectedProject.id : ''],
      4,
      2
    ]);
    // Zelfde volgorde als in de stijl: selectie (geel) wint, daarna de soort.
    map.setPaintProperty('projecten-point', 'circle-stroke-color', [
      'case',
      ['==', ['get', 'id'], selectedProject ? selectedProject.id : ''], '#ffff00',
      isVervangendExpr(), '#1971c2',
      isOmbouwExpr(), '#868e96',
      '#fff'
    ]);
  }, [selectedProject]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let kadastraalLoaded = false;

    function updateKadastraalSource() {
      if (level === 'project' && map.getZoom() >= 17) {
        if (map.getSource('kadastraal') && !kadastraalLoaded) {
          // Haal de huidige bbox op in WGS84 en maak deze 50% groter
          const bounds = map.getBounds();
          let minLat = bounds.getSouth();
          let minLng = bounds.getWest();
          let maxLat = bounds.getNorth();
          let maxLng = bounds.getEast();
          // Vergroot bbox 50% in alle richtingen
          const dLat = (maxLat - minLat) * 0.5;
          const dLng = (maxLng - minLng) * 0.5;
          minLat -= dLat;
          maxLat += dLat;
          minLng -= dLng;
          maxLng += dLng;
          const bbox = [minLat, minLng, maxLat, maxLng].join(',');
          map.getSource('kadastraal').setData(`/api/kadastraal-wfs?bbox=${bbox}`);
          kadastraalLoaded = true;
        }
      } else {
        if (map.getSource('kadastraal')) {
          map.getSource('kadastraal').setData({ type: 'FeatureCollection', features: [] });
          kadastraalLoaded = false;
        }
      }
    }

    // Initieel checken
    updateKadastraalSource();
    // Bij elke zoom of moveend wijziging opnieuw checken
    map.on('zoomend', updateKadastraalSource);
    map.on('moveend', updateKadastraalSource);
    // Ook bij level-wijziging opnieuw checken
    return () => {
      map.off('zoomend', updateKadastraalSource);
      map.off('moveend', updateKadastraalSource);
    };
  }, [level]);

  // Boomkronen zoom-gebaseerd laden (zoals kadastrale laag)
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    let boomkronenLoaded = false;

    async function updateBoomkronenSource() {
      if (level === 'project' && map.getZoom() >= 17) {
        if (map.getSource('boomkronen') && !boomkronenLoaded) {
          try {
            // Haal de huidige bbox op in WGS84 en maak deze 50% groter (zoals kadastrale systeem)
            const bounds = map.getBounds();
            let minLat = bounds.getSouth();
            let minLng = bounds.getWest();
            let maxLat = bounds.getNorth();
            let maxLng = bounds.getEast();
            // Vergroot bbox 50% in alle richtingen
            const dLat = (maxLat - minLat) * 0.5;
            const dLng = (maxLng - minLng) * 0.5;
            minLat -= dLat;
            maxLat += dLat;
            minLng -= dLng;
            maxLng += dLng;
            const bbox = [minLng, minLat, maxLng, maxLat].join(',');
            
            // Laad boomkronen data
            const boomkronenResp = await fetchWithAuth(`/api/arcgis/boomkronen?bbox=${bbox}`);
            if (boomkronenResp.ok) {
              const boomkronenData = await boomkronenResp.json();
              map.getSource('boomkronen').setData(boomkronenData);
              
              // Maak zichtbaar
              if (map.getLayer('boomkronen-fill')) map.setLayoutProperty('boomkronen-fill', 'visibility', 'visible');
              if (map.getLayer('boomkronen-line')) map.setLayoutProperty('boomkronen-line', 'visibility', 'visible');
              
              boomkronenLoaded = true;
            }
          } catch (error) {
            console.warn('Kon boomkronen data niet laden:', error);
            addToast('Fout bij laden van boomkronen', 'error');
          }
        }
      } else {
        if (map.getSource('boomkronen')) {
          // Leeg data en verberg bij uitzoomen of level wijziging
          map.getSource('boomkronen').setData({ type: 'FeatureCollection', features: [] });
          if (map.getLayer('boomkronen-fill')) map.setLayoutProperty('boomkronen-fill', 'visibility', 'none');
          if (map.getLayer('boomkronen-line')) map.setLayoutProperty('boomkronen-line', 'visibility', 'none');
          boomkronenLoaded = false;
        }
      }
    }

    // Initieel checken
    updateBoomkronenSource();
    // Bij elke zoom of moveend wijziging opnieuw checken
    map.on('zoomend', updateBoomkronenSource);
    map.on('moveend', updateBoomkronenSource);
    
    return () => {
      map.off('zoomend', updateBoomkronenSource);
      map.off('moveend', updateBoomkronenSource);
    };
  }, [level]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Zorg dat de Bebouwing-laag altijd boven de kadastraal-line staat
    if (map.getLayer('kadastraal-bebouwing') && map.getLayer('kadastraal-line')) {
      map.moveLayer('kadastraal-bebouwing', 'kadastraal-line');
    }
  }, [level]);

  // Voeg bovenaan je App component toe:
  const sidebarOpenRef = useRef(sidebarOpen);
  const selectedProjectRef = useRef(selectedProject);
  const selectedBuurtRef = useRef(selectedBuurt); 
  useEffect(() => { sidebarOpenRef.current = sidebarOpen; }, [sidebarOpen]);
  useEffect(() => { selectedProjectRef.current = selectedProject; }, [selectedProject]);
  useEffect(() => { selectedBuurtRef.current = selectedBuurt; }, [selectedBuurt]);

  // Regio highlighting effect
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Pas de lijnstijl van regios-line aan op basis van geselecteerde regio
    if (map.getLayer('regios-line')) {
      map.setPaintProperty('regios-line', 'line-color', [
        'case',
        selectedRegio ? ['==', ['get', 'regio'], selectedRegio.regio] : false, '#ffe600',
        '#000000'
      ]);
      map.setPaintProperty('regios-line', 'line-width', [
        'case',
        selectedRegio ? ['==', ['get', 'regio'], selectedRegio.regio] : false, 4,
        2
      ]);
    }
  }, [selectedRegio]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Pas de lijnstijl van buurten-line aan op basis van geselecteerde buurt
    if (map.getLayer('buurten-line')) {
      map.setPaintProperty('buurten-line', 'line-color', [
        'case',
        selectedBuurt ? ['==', ['get', 'buurtcode'], selectedBuurt.buurtcode] : false, '#ffe600',
        '#000000'
      ]);
      map.setPaintProperty('buurten-line', 'line-width', [
        'case',
        selectedBuurt ? ['==', ['get', 'buurtcode'], selectedBuurt.buurtcode] : false, 6,
        1
      ]);
    }
  }, [selectedBuurt]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Ctrl+klik of addStationMode+klik op de kaart om een nieuw station te plaatsen
    function handleCtrlClick(e) {
      if (level !== 'buurt') return;
      // Check voor Ctrl+click OF addStationMode actief
      if (!e.originalEvent.ctrlKey && !addStationMode) return;

      // Haal de buurtcode en buurtnaam van de laatst aangeklikte buurtfeature
      let buurtcode = null;
      let buurtnaam = null;

      // Probeer eerst de buurtFeatureRef
      if (buurtFeatureRef.current && buurtFeatureRef.current.properties) {
        buurtcode = buurtFeatureRef.current.properties.buurtcode;
        buurtnaam = buurtFeatureRef.current.properties.buurtnaam;
      } else {
        // Als buurtFeatureRef leeg is, query de buurt features op de kliklocatie
        const buurtFeatures = map.queryRenderedFeatures(e.point, {
          layers: ['buurten-fill']
        });

        if (buurtFeatures.length > 0) {
          buurtcode = buurtFeatures[0].properties.buurtcode;
          buurtnaam = buurtFeatures[0].properties.buurtnaam;
        }
      }

      // Sluit alle andere zijpanelen
      setSelectedRegio(null);
      setSelectedGemeente(null);
      setSelectedBuurt(null);
      setSelectedProject(null);

      setNewStation({ lng: e.lngLat.lng, lat: e.lngLat.lat, buurtcode, buurtnaam });
      setSidebarOpen(true);
      // Schakel addStationMode uit na plaatsen (volgens specificatie)
      if (addStationMode) {
        setAddStationMode(false);
      }
    }
    map.on('click', handleCtrlClick);
    return () => {
      map.off('click', handleCtrlClick);
    };
  }, [level, addStationMode, deleteStationMode]);

  // Delete station click handler
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    function handleDeleteClick(e) {
      if (level !== 'buurt') return;
      if (!deleteStationMode) return;

      // Check of er een project/station werd aangeklikt
      const projectFeatures = map.queryRenderedFeatures(e.point, {
        layers: ['projecten-point', 'laagspanningsstations-point']
      });

      if (projectFeatures.length > 0) {
        const feature = projectFeatures[0];
        const projectnummer = feature.properties.projectnummer || feature.properties.id;
        const projectnaam = feature.properties.projectnummer ?
          `Project ${feature.properties.projectnummer}` :
          `Station ${feature.properties.id}`;

        // Bevestiging dialog
        if (window.confirm(`${projectnaam} verwijderen?`)) {
          deleteProject(projectnummer);
        }

        // Schakel deleteStationMode uit na klik
        setDeleteStationMode(false);
      }
    }

    map.on('click', handleDeleteClick);
    return () => {
      map.off('click', handleDeleteClick);
    };
  }, [level, deleteStationMode]);

  // Modus uit (of niveau verlaten) => halve koppeling weggooien.
  useEffect(() => {
    if (!koppelMode || level !== 'buurt') setKoppelBron(null);
  }, [koppelMode, level]);

  // Koppelen: klik 1 = het vervangende project, klik 2 = het bestaande station
  // dat daardoor verdwijnt. Zelfde opzet als de verwijder-modus hierboven.
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    async function handleKoppelClick(e) {
      if (level !== 'buurt' || !koppelMode) return;
      const treffers = map.queryRenderedFeatures(e.point, {
        layers: ['projecten-point', 'laagspanningsstations-point'],
      });
      if (!treffers.length) return;
      const f = treffers[0];
      const pnr = f.properties.projectnummer;

      if (!koppelBron) {
        if (!pnr) {
          addToast('Klik eerst het vervangende station aan (de gekleurde stip)', 'error');
          return;
        }
        if (!/vervangen/i.test(String(f.properties.type || ''))) {
          addToast('Dit is geen vervangend station. Zet "Type werk" op een vervangen station.', 'error');
          return;
        }
        setKoppelBron(pnr);
        addToast('Klik nu het bestaande station aan dat vervangen wordt', 'info');
        return;
      }

      if (pnr) {
        addToast('Klik een bestaand station (grijze stip), geen project', 'error');
        return;
      }
      try {
        const res = await fetchWithAuth(`/api/projecten/${encodeURIComponent(koppelBron)}/vervangt`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ station_id: f.properties.id }),
        });
        const data = await res.json();
        if (!data.success) throw new Error(data.error);
        addToast('Te vervangen station gekoppeld', 'success');
        await ververBuurtVervangingen();
      } catch (err) {
        addToast('Koppelen mislukt', 'error');
      }
      setKoppelBron(null);
      setKoppelMode(false);
    }

    map.on('click', handleKoppelClick);
    return () => {
      map.off('click', handleKoppelClick);
    };
  }, [level, koppelMode, koppelBron, fetchWithAuth, addToast, ververBuurtVervangingen]);

  // Delete project function
  const deleteProject = async (projectnummer) => {
    try {
      const resp = await fetchWithAuth(`/api/projecten/${projectnummer}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' }
      });

      if (resp.ok) {
        // Herlaad de kaartlagen om het verwijderde project te verbergen
        const map = mapRef.current;
        if (map && buurtFeatureRef.current) {
          const buurtCode = buurtFeatureRef.current.properties.buurtcode;

          // Herlaad projecten
          const respProjects = await fetchWithAuth(`/api/projecten?buurtcode=${encodeURIComponent(buurtCode)}`);
          const dataProjects = await respProjects.json();
          const stippenData = bouwProjectStippen(dataProjects && dataProjects.features);

          map.getSource('projecten').setData(stippenData);

          // Sluit zijpaneel als het verwijderde project geselecteerd was
          if (selectedProject && selectedProject.projectnummer === projectnummer) {
            setSelectedProject(null);
            setSidebarOpen(false);
          }
        }
      } else {
        addToast('Fout bij verwijderen van project', 'error');
      }
    } catch (e) {
      console.error('Error deleting project:', e);
      addToast('Netwerkfout bij verwijderen', 'error');
    }
  };

  // Cursor management voor addStationMode en deleteStationMode
  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (addStationMode && level === 'buurt') {
      // Zet cursor naar crosshair voor station plaatsen
      map.getCanvas().style.cursor = 'crosshair';

      return () => {
        // Reset cursor bij cleanup
        map.getCanvas().style.cursor = '';
      };
    }

    if (deleteStationMode && level === 'buurt') {
      // Zet cursor naar crosshair voor station verwijderen
      map.getCanvas().style.cursor = 'crosshair';

      return () => {
        // Reset cursor bij cleanup
        map.getCanvas().style.cursor = '';
      };
    }
  }, [addStationMode, deleteStationMode, level]);

  // Add search handler functions before the return statement
  // Serverkant-zoeken: één endpoint, gemeente-gescoped, met resultaatlimiet.
  const handleSearch = useCallback(async (term) => {
    if (!term || term.trim().length < 2) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }
    try {
      const response = await fetchWithAuth(`/api/search?q=${encodeURIComponent(term.trim())}`);
      const data = await response.json();
      if (data.success) {
        setSearchResults(data.results);
        setShowSearchResults(true);
      }
    } catch (error) {
      console.error('Zoeken mislukt:', error);
      addToast('Fout bij zoeken', 'error');
    }
  }, [fetchWithAuth, addToast]);

  const handleResultSelect = async (result) => {
    const map = mapRef.current;
    if (!map) return;

    setShowSearchResults(false);
    // Oude adres-marker opruimen bij elke nieuwe selectie
    if (adresMarkerRef.current) { adresMarkerRef.current.remove(); adresMarkerRef.current = null; }

    switch (result.type) {
      case 'adres':
        // Adres (PDOK): eerst naar de buurt die het adres bevat (buurtniveau,
        // laadt stations e.d.), dan inzoomen op het exacte adres met een marker.
        if (result.feature.geometry) {
          const [lng, lat] = result.feature.geometry.coordinates;
          try {
            const resp = await fetchWithAuth(`/api/buurt-op-punt?lng=${lng}&lat=${lat}`);
            const d = await resp.json();
            if (d.success && d.feature) {
              setSelectedProject(null);
              await drillDown({ targetLevel: 'buurt', feature: d.feature });
            }
          } catch (e) { /* geen buurt gevonden: gewoon inzoomen op het adres */ }
          const flyToOptions = { center: [lng, lat], zoom: 18, pitch: 0, duration: 1200 };
          if (sidebarOpen) flyToOptions.offset = [175, 0];
          map.flyTo(flyToOptions);
          adresMarkerRef.current = new maplibregl.Marker({ color: '#e8590c' })
            .setLngLat([lng, lat])
            .setPopup(new maplibregl.Popup({ offset: 24 }).setText(result.name || 'Adres'))
            .addTo(map);
          adresMarkerRef.current.togglePopup();
        }
        break;

      case 'project':
        // Station: volledige afdaling naar projectniveau (kruimelpad + kaartlagen).
        if (result.feature.geometry) daalAfNaarProject(result.feature);
        break;
        
      case 'gemeente':
        // Oude selecties wissen, anders houdt het project/buurt-venster voorrang
        setSelectedProject(null);
        setSelectedBuurt(null);
        setSelectedRegio(null);
        drillDown({ targetLevel: 'gemeente', feature: result.feature });
        setSelectedGemeente(result.feature.properties);
        setSidebarOpen(true);
        break;

      case 'buurt':
        setSelectedProject(null);
        drillDown({ targetLevel: 'buurt', feature: result.feature });
        break;
    }
  };

  // Zoek een term en navigeer meteen naar het beste resultaat, alsof het in de
  // zoekbalk was ingevuld. Gebruikt door de 'Betreft'-kolom in Recente
  // wijzigingen. Doel-suffixen als ' v2' (versie) of ' locatie 3' worden
  // gestript zodat een projectnummer overblijft.
  const zoekEnGaNaar = async (rawTerm) => {
    const term = (rawTerm || '')
      .replace(/\s+v\d+$/i, '')
      .replace(/\s+locatie\s+\d+$/i, '')
      .trim();
    if (term.length < 2) return;
    try {
      const resp = await fetchWithAuth(`/api/search?q=${encodeURIComponent(term)}`);
      const data = await resp.json();
      const results = (data && data.results) || [];
      if (!results.length) {
        addToast('Niets gevonden om naartoe te navigeren', 'info');
        return;
      }
      const lc = term.toLowerCase();
      const exact = results.find(r => (r.name || r.id || '').toLowerCase() === lc);
      const nietAdres = results.find(r => r.type !== 'adres');
      handleResultSelect(exact || nietAdres || results[0]);
    } catch (e) {
      addToast('Zoeken mislukt', 'error');
    }
  };

  // --- UI rendering: loading screen tijdens token validatie, daarna kaart als ingelogd, anders login/register ---
  if (isLoading) {
    return (
      <Center h="100vh" bg="gray.0">
        <Stack align="center" gap="md">
          <Loader color="groen" size="lg" />
          <Text c="dimmed">Controleren sessie...</Text>
        </Stack>
      </Center>
    );
  }

  // Resetlink uit de e-mail (?reset=<token>) heeft voorrang op het loginscherm
  const resetToken = !user ? new URLSearchParams(window.location.search).get('reset') : null;
  if (resetToken) {
    return (
      <WachtwoordResetPage
        token={resetToken}
        onKlaar={() => { window.location.href = window.location.pathname; }}
      />
    );
  }

  return (
    <div className="App">
      {(!user && showLogin) && (
        <LoginPage onLogin={handleLogin} onShowRegister={handleShowRegister} />
      )}
      {(!user && showRegister) && (
        <RegisterPage onRegisterSuccess={handleRegisterSuccess} onShowLogin={() => { setShowRegister(false); setShowLogin(true); }} />
      )}
      {user && (
        <AppContext.Provider value={{
          mapRef, level, user, fetchWithAuth, drillDown, goBack, daalAfNaarProject,
          zoekEnGaNaar,
          sidebarOpen, setSidebarOpen,
          baseLayer, setBaseLayer, baseLayerRef,
          selectedProject, setSelectedProject,
          actieveVersieId, setActieveVersieId, versieRefresh, bumpVersieRefresh,
          selectedBuurt, setSelectedBuurt,
          selectedGemeente, setSelectedGemeente,
          selectedRegio, setSelectedRegio,
          newStation, setNewStation,
          handleProjectFieldChange,
          buurtOptions,
          stationstypeOptions,
          typeOptions,
          kleurstellingOptions,
          gemeenteOptions,
          jaren, minJaar, maxJaar, selectedYear, onYearClick,
          regioJaarActief, setRegioJaarActief,
          addToast,
          sessie,
          ververBuurtVervangingen,
          ververBuurtProjecten,
          navigatie: {
            regio: regioFeatureRef,
            gemeente: gemeenteFeatureRef,
            buurt: buurtFeatureRef,
          }
        }}>
          <MapToolbar
            sidebarOpen={sidebarOpen}
            setSidebarOpen={setSidebarOpen}
            level={level}
            addStationMode={addStationMode}
            setAddStationMode={setAddStationMode}
            deleteStationMode={deleteStationMode}
            setDeleteStationMode={setDeleteStationMode}
            moveStationMode={moveStationMode}
            setMoveStationMode={setMoveStationMode}
            koppelMode={koppelMode}
            setKoppelMode={setKoppelMode}
            percelenOverlay={percelenOverlay}
            setPercelenOverlay={setPercelenOverlay}
            user={user}
          />
          <SearchBar
            showSearchResults={showSearchResults}
            searchResults={searchResults}
            onResultSelect={handleResultSelect}
            onSearch={handleSearch}
            onClear={() => { setSearchResults([]); setShowSearchResults(false); }}
          />
          {/* Map alleen tonen als ingelogd */}
          <div ref={mapContainer} className="map-container" style={{ width: '100vw', height: '100vh', position: 'absolute', top: 0, left: 0, zIndex: 1 }} />
          <Sidebar />
          <SessieBalk />
          <UserMenu onLogout={handleLogout} />
          <NavigatiePad />
          {/* Edit mode banner */}
          <div id="edit-modus-banner" style={{
            display: editModus ? 'block' : 'none',
            position: 'absolute', top: 10, left: 10,
            background: '#ffc107', color: '#000', padding: '5px 10px', zIndex: 2
          }}>
            Editmodus actief
          </div>
          {/* Excel dropzone */}
          <div id="excel-dropzone" ref={dropzoneRef} style={{
            display: editModus ? 'flex' : 'none',
            position: 'absolute', top: 50, right: 10,
            width: '200px', height: '100px',
            background: '#f8f8f8', border: '2px dashed #000',
            alignItems: 'center', justifyContent: 'center', zIndex: 2
          }}>
            <span id="excel-upload-status" ref={statusRef}>Drop je Excel hier</span>
          </div>
          <LegendaPaneel
            level={level}
            isStedin={!!user.gemeentenaam && user.gemeentenaam.toLowerCase() === 'stedin'}
            jaren={jaren}
            minJaar={minJaar}
            maxJaar={maxJaar}
            selectedYear={selectedYear}
            onYearClick={onYearClick}
            regioJaarActief={regioJaarActief}
            klicThemas={klicThemas}
          />
        </AppContext.Provider>
      )}
    </div>
  );
}

export default App;
